import * as React from 'react';
import moment from 'moment';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from 'Src/models/tournament';
import {
  getAllTournamentEligibleSchools,
  getTournamentEligibleSchools,
  getTournamentEligibleSchoolsCount,
  getTournamentManagementSummary,
  updateTournamentAutoEnrollment
} from 'Src/helpers/service/adminSU/tournamentsTable';
import {
  addTournamentParticipants,
  getAllTournamentParticipants,
  getConfirmationRequestTemplate,
  getTournamentParticipants,
  getTournamentParticipantsCount,
  moveTournamentParticipantsToWaitingList,
  moveTournamentWaitingListToParticipants,
  removeTournamentParticipant,
  sendConfirmationRequest
} from 'Src/helpers/service/adminSU/tournamentParticipantsTable';
import { addTournamentTeamsBatch } from 'Src/helpers/service/admin/tournamentTeams';
import { getAllSchoolUnionTournamentTeams } from 'Src/helpers/service/adminSU/tournamentTeamsTable';
import { sendTournamentInvite } from 'Src/helpers/service/adminSU/tournamentEligibleSchoolsTable';
import { getTournamentCapacitySummary, isIndividualTournament } from 'Src/helpers/tournament/tournament';
import { GENDER } from 'Src/consts/common';
import { TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING, TOURNAMENT_SCHOOL_LOGO_FALLBACK } from 'Src/consts/tournament';
import { TEAM_TYPE } from 'Src/consts/team';
import {
  addTournamentWaitingListSchool,
  getTournamentWaitingList,
  getTournamentWaitingListCount,
  removeTournamentWaitingListSchool
} from 'Src/helpers/service/adminSU/tournamentWaitingListTable';
import {
  getTournamentConfirmationRequests,
  getTournamentConfirmationRequestsCount,
  getAllTournamentConfirmationRequests
} from 'Src/helpers/service/adminSU/tournamentConfirmationRequestsTable';
import { formDefinitionToLegacyFields } from 'Src/helpers/form/legacyFormAdapter';
import { ConfirmationRequestForm } from 'Src/components/ConfirmationRequestForm/ConfirmationRequestForm';
import { INPUT_MASK_VALIDATION } from 'Src/consts/date';
import { UnsavedChangesModal } from 'Src/components/UnsavedChangesModal/UnsavedChangesModal';
import { getResponseUser, getRespondedAtText } from 'Src/helpers/message/message';
import { CONFIRMATION_REQUESTS_STATUS } from 'Src/consts/message';
import {
  createCommentEnrollmentLog,
  getAllTournamentEnrollmentLogs,
  getTournamentEnrollmentLogs,
  getTournamentEnrollmentLogsCount
} from 'Src/helpers/service/adminSU/tournamentEnrollmentLogsTable';
import { TOURNAMENT_LOG_ACTION_TYPES, TOURNAMENT_LOG_ACTION_TYPES_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/log';
import { EligibleSchool } from 'Src/models/school';
import { TournamentWaitingListItem } from 'Src/models/tournamentWaitingList';
import { AGE_GROUPS, KIND } from 'Src/consts/school';
import { INVITE_STATUS } from 'Src/consts/invite';
import { getAllSchoolUnionSchools } from 'Src/helpers/service/adminSU/schoolsTable';
import {
  getAllTournamentAdmins,
  getTournamentAdmins,
  getTournamentAdminsCount
} from 'Src/helpers/service/adminSU/tournamentAdminsTable';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { getTournamentEvents } from 'Src/helpers/service/adminSU/tournamentEventsTable';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import { DatePicker as SitDatePicker, DateRange } from 'Src/components/DatePicker/DatePicker';
import { formatTeamAgesCompact } from 'Src/helpers/ageGroups';
import '../Tournaments.css';
import {
  getSchoolAvailableAgesGroup,
  getSchoolAvailableGender,
  getTournamentInviteStatus
} from 'Src/helpers/accessor/accessor';

import { hasUnionParticipant } from 'Src/helpers/tournament/athleteSchools';
import { getTournamentNotificationAthleteSchools } from 'Src/helpers/service/adminSU/tournamentNotificationTable';
import { TournamentAthleteSchoolsEntries } from './TournamentAthleteSchoolsEntries';

export type EntriesSection = 'rules' | 'eligible' | 'participants' | 'athleteSchools' | 'schoolAdmins' | 'waiting' | 'requests' | 'log';

interface Props {
  user: AppUser;
  tournament: Tournament;
  activeSection: EntriesSection;
  onSectionChange: (section: EntriesSection) => void;
  onEditRules: () => void;
  onOpenLegacySection: (path: string) => void;
  onNotifySchools: (schools: EligibleSchool[]) => void;
  onNotifyAthleteSchools?: (schools: EligibleSchool[]) => void;
  onUpdated: (tournament: Tournament) => void;
  hideSectionNavigation?: boolean;
}

interface RulesDraft {
  isReadOnly: boolean;
  isAutoEnrollmentTeams: boolean;
  isEnableWaitingListWhenFull: boolean;
  isAutoEnrollmentToWaitingList: boolean;
  maximumNumberTeamsOfSchool: number;
  individualCapacity: number;
  settings: Array<{ age: number; gender: string; maximumNumberTeams: number }>;
}
type RulesBooleanField =
  | 'isReadOnly'
  | 'isAutoEnrollmentTeams'
  | 'isEnableWaitingListWhenFull'
  | 'isAutoEnrollmentToWaitingList';

interface Counts {
  athleteSchools: number;
  eligible: number;
  participants: number;
  schoolAdmins: number;
  waiting: number;
  requests: number;
  log: number;
}

interface State {
  counts: Counts;
  eligibleSchools: EligibleSchool[];
  eligiblePage: number;
  eligibleSearch: string;
  eligibleSearchDraft: string;
  selectedSchoolIds: string[];
  selectedSchools: EligibleSchool[];
  isEligibleLoading: boolean;
  isActionsOpen: boolean;
  isFiltersOpen: boolean;
  isColumnsOpen: boolean;
  isSelectMenuOpen: boolean;
  isRemoveConfirmationOpen: boolean;
  isRemovingParticipants: boolean;
  removeParticipantsError: string;
  isAddConfirmationOpen: boolean;
  isAddingParticipants: boolean;
  addParticipantsError: string;
  participantEvents: any[];
  selectedParticipantEventIds: string[];
  participantEventSportSearch: string;
  isParticipantEventsLoading: boolean;
  isParticipantLimitWarningOpen: boolean;
  participantLimitWarning: string;
  isInviteConfirmationOpen: boolean;
  isSendingInvites: boolean;
  sendInvitesError: string;
  isWaitingListConfirmationOpen: boolean;
  isAddingToWaitingList: boolean;
  addToWaitingListError: string;
  selectedWaitingIds: string[];
  selectedWaitingItems: TournamentWaitingListItem[];
  isRemoveWaitingOpen: boolean;
  isRemovingFromWaiting: boolean;
  removeFromWaitingError: string;
  isMoveWaitingOpen: boolean;
  isMovingWaiting: boolean;
  moveWaitingError: string;
  moveWaitingCapacityWarning: string;
  areas: any[];
  filters: {
    ages: number[];
    genders: string[];
    participant: string;
    statuses: string[];
    dateFrom: string;
    dateTo: string;
    areas: string[];
  };
  eligibleFilterDraft: {
    ages: number[];
    genders: string[];
    participant: string;
    statuses: string[];
    dateFrom: string;
    dateTo: string;
    areas: string[];
  };
  visibleColumns: string[];
  participants: any[];
  participantsPage: number;
  participantsSearch: string;
  participantsSearchDraft: string;
  isParticipantsLoading: boolean;
  participantsUnfilteredTotal: number | null;
  selectedParticipantIds: string[];
  selectedParticipantItems: any[];
  isMoveParticipantsOpen: boolean;
  isMovingParticipants: boolean;
  moveParticipantsError: string;
  participantTeamCounts: { [schoolId: string]: number };
  participantEntryMeta: { [schoolId: string]: any };
  schoolAdmins: any[];
  schoolAdminsSearch: string;
  schoolAdminsSearchDraft: string;
  isSchoolAdminsLoading: boolean;
  schoolAdminsPage: number;
  selectedSchoolAdminIds: string[];
  schoolAdminFilters: { schools: string[]; contacts: string[] };
  schoolAdminFilterDraft: { schools: string[]; contacts: string[] };
  isSchoolAdminFiltersOpen: boolean;
  isSchoolAdminColumnsOpen: boolean;
  schoolAdminVisibleColumns: string[];
  waitingItems: TournamentWaitingListItem[];
  waitingPage: number;
  waitingSearch: string;
  waitingSearchDraft: string;
  isWaitingLoading: boolean;
  waitingEntryMeta: { [schoolId: string]: any };
  confirmationRequests: any[];
  confirmationPage: number;
  confirmationSearch: string;
  confirmationSearchDraft: string;
  confirmationStatus: string;
  confirmationStatusDraft: string;
  isConfirmationLoading: boolean;
  isConfirmationFiltersOpen: boolean;
  isConfirmationColumnsOpen: boolean;
  confirmationVisibleColumns: string[];
  openedConfirmationRequest?: any;
  isSendRequestsOpen: boolean;
  confirmationRecipients: any[];
  selectedConfirmationRecipientIds: string[];
  confirmationTemplate?: any;
  confirmationDeadline: Date | null;
  isSendingConfirmationRequests: boolean;
  confirmationSendError: string;
  confirmationSendStep: 'form' | 'recipients';
  preparedConfirmationFields: any[];
  preparedConfirmationFormDefinition?: any;
  isDiscardConfirmationSendOpen: boolean;
  enrollmentLogs: any[];
  enrollmentLogPage: number;
  enrollmentLogSearch: string;
  enrollmentLogSearchDraft: string;
  enrollmentLogAction: string[];
  enrollmentLogDateRange: DateRange;
  enrollmentLogActionDraft: string[];
  enrollmentLogDateRangeDraft: DateRange;
  isEnrollmentLogLoading: boolean;
  selectedEnrollmentLogId: string;
  isEnrollmentLogFiltersOpen: boolean;
  isEnrollmentLogColumnsOpen: boolean;
  enrollmentLogVisibleColumns: string[];
  isEnrollmentLogCommentOpen: boolean;
  enrollmentLogComment: string;
  isEnrollmentLogCommentSaving: boolean;
  openedEnrollmentLog?: any;
  isDiscardEnrollmentLogCommentOpen: boolean;
  isRulesEditorOpen: boolean;
  isRulesSaving: boolean;
  isDiscardRulesOpen: boolean;
  rulesError: string;
  rulesDraft?: RulesDraft;
}

const safeCount = (request: Promise<{ count: number }>): Promise<number> =>
  request
    .then(result => {
      const raw: any = result && result.count;
      const normalized = typeof raw === 'object' && raw !== null ? raw.count : raw;
      const value = Number(normalized);
      return isFinite(value) ? value : 0;
    })
    .catch(() => 0);

const getRequestErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.details?.text ||
  error?.response?.data?.error?.message ||
  error?.response?.data?.message ||
  fallback;

const getParticipantMoveError = (error: any) => {
  const message = getRequestErrorMessage(error, 'Could not be moved to the waiting list.');
  if (message.includes('Have groups with participant')) {
    return 'Its teams are assigned to competition groups. Remove those assignments first.';
  }
  if (message.includes('Have events with participant')) {
    return 'Its teams are used in fixtures or tournament events. Remove those links first.';
  }
  return message;
};

const Icon = ({ path, size = 14 }: { path: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d={path} />
  </svg>
);

const TableEmptyState = ({
  filtered,
  emptyTitle,
  emptyText,
  filteredTitle,
  filteredText,
  icon
}: {
  filtered: boolean;
  emptyTitle: string;
  emptyText: string;
  filteredTitle: string;
  filteredText: string;
  icon: string;
}) => (
  <div className="utmTableEmptyState">
    <span aria-hidden="true"><Icon path={filtered ? 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16 16l4 4' : icon} /></span>
    <strong>{filtered ? filteredTitle : emptyTitle}</strong>
    <p>{filtered ? filteredText : emptyText}</p>
  </div>
);

const ICONS = {
  edit: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16 16l4 4'
};

const DEFAULT_ELIGIBLE_COLUMNS = ['school', 'ages', 'gender', 'participant', 'status', 'date', 'area'];
const DEFAULT_ENROLLMENT_LOG_COLUMNS = ['action', 'school', 'team', 'by', 'comment'];
const DEFAULT_CONFIRMATION_COLUMNS = ['status', 'respondedBy', 'respondedAt', 'deadline'];
const confirmationColumnsStorageKey = (userId: string) => `unionTournamentConfirmationColumns:${userId || 'anonymous'}`;

const loadConfirmationColumns = (userId: string): string[] => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(confirmationColumnsStorageKey(userId)) || '[]');
    return Array.isArray(saved) && saved.length ? saved : DEFAULT_CONFIRMATION_COLUMNS;
  } catch {
    return DEFAULT_CONFIRMATION_COLUMNS;
  }
};

const SECTIONS: Array<{ key: EntriesSection; label: string; count?: keyof Counts }> = [
  { key: 'rules', label: 'Rules & capacity' },
  { key: 'eligible', label: 'Eligible schools', count: 'eligible' },
  { key: 'participants', label: 'Participants', count: 'participants' },
  { key: 'athleteSchools', label: 'Athletes’ schools', count: 'athleteSchools' },
  { key: 'schoolAdmins', label: 'School admins', count: 'schoolAdmins' },
  { key: 'waiting', label: 'Waiting list', count: 'waiting' },
  { key: 'log', label: 'Enrolment log', count: 'log' }
];

export class TournamentEntries extends React.Component<Props, State> {
  state: State = {
    counts: { athleteSchools: 0, eligible: 0, participants: 0, schoolAdmins: 0, waiting: 0, requests: 0, log: 0 },
    eligibleSchools: [],
    eligiblePage: 1,
    eligibleSearch: '',
    eligibleSearchDraft: '',
    selectedSchoolIds: [],
    selectedSchools: [],
    isEligibleLoading: true,
    isActionsOpen: false,
    isFiltersOpen: false,
    isColumnsOpen: false,
    isSelectMenuOpen: false,
    isRemoveConfirmationOpen: false,
    isRemovingParticipants: false,
    removeParticipantsError: '',
    isAddConfirmationOpen: false,
    isAddingParticipants: false,
    addParticipantsError: '',
    participantEvents: [],
    selectedParticipantEventIds: [],
    participantEventSportSearch: '',
    isParticipantEventsLoading: false,
    isParticipantLimitWarningOpen: false,
    participantLimitWarning: '',
    isInviteConfirmationOpen: false,
    isSendingInvites: false,
    sendInvitesError: '',
    isWaitingListConfirmationOpen: false,
    isAddingToWaitingList: false,
    addToWaitingListError: '',
    selectedWaitingIds: [],
    selectedWaitingItems: [],
    isRemoveWaitingOpen: false,
    isRemovingFromWaiting: false,
    removeFromWaitingError: '',
    isMoveWaitingOpen: false,
    isMovingWaiting: false,
    moveWaitingError: '',
    moveWaitingCapacityWarning: '',
    areas: [],
    filters: { ages: [], genders: [], participant: '', statuses: [], dateFrom: '', dateTo: '', areas: [] },
    eligibleFilterDraft: { ages: [], genders: [], participant: '', statuses: [], dateFrom: '', dateTo: '', areas: [] },
    visibleColumns: [...DEFAULT_ELIGIBLE_COLUMNS],
    participants: [],
    participantsPage: 1,
    participantsSearch: '',
    participantsSearchDraft: '',
    isParticipantsLoading: true,
    participantsUnfilteredTotal: null,
    selectedParticipantIds: [],
    selectedParticipantItems: [],
    isMoveParticipantsOpen: false,
    isMovingParticipants: false,
    moveParticipantsError: '',
    participantTeamCounts: {},
    participantEntryMeta: {},
    schoolAdmins: [],
    schoolAdminsSearch: '',
    schoolAdminsSearchDraft: '',
    isSchoolAdminsLoading: true,
    schoolAdminsPage: 1,
    selectedSchoolAdminIds: [],
    schoolAdminFilters: { schools: [], contacts: [] },
    schoolAdminFilterDraft: { schools: [], contacts: [] },
    isSchoolAdminFiltersOpen: false,
    isSchoolAdminColumnsOpen: false,
    schoolAdminVisibleColumns: ['email', 'phone', 'school'],
    waitingItems: [],
    waitingPage: 1,
    waitingSearch: '',
    waitingSearchDraft: '',
    isWaitingLoading: true,
    waitingEntryMeta: {},
    confirmationRequests: [],
    confirmationPage: 1,
    confirmationSearch: '',
    confirmationSearchDraft: '',
    confirmationStatus: '',
    confirmationStatusDraft: '',
    isConfirmationLoading: true,
    isConfirmationFiltersOpen: false,
    isConfirmationColumnsOpen: false,
    confirmationVisibleColumns: loadConfirmationColumns(this.props.user.userId),
    openedConfirmationRequest: undefined,
    isSendRequestsOpen: false,
    confirmationRecipients: [],
    selectedConfirmationRecipientIds: [],
    confirmationTemplate: undefined,
    confirmationDeadline: null,
    isSendingConfirmationRequests: false,
    confirmationSendError: '',
    confirmationSendStep: 'form',
    preparedConfirmationFields: [],
    preparedConfirmationFormDefinition: undefined,
    isDiscardConfirmationSendOpen: false,
    enrollmentLogs: [],
    enrollmentLogPage: 1,
    enrollmentLogSearch: '',
    enrollmentLogSearchDraft: '',
    enrollmentLogAction: [],
    enrollmentLogDateRange: { start: null, end: null },
    enrollmentLogActionDraft: [],
    enrollmentLogDateRangeDraft: { start: null, end: null },
    isEnrollmentLogLoading: true,
    selectedEnrollmentLogId: '',
    isEnrollmentLogFiltersOpen: false,
    isEnrollmentLogColumnsOpen: false,
    enrollmentLogVisibleColumns: [...DEFAULT_ENROLLMENT_LOG_COLUMNS],
    isEnrollmentLogCommentOpen: false,
    enrollmentLogComment: '',
    isEnrollmentLogCommentSaving: false,
    isDiscardEnrollmentLogCommentOpen: false,
    isRulesEditorOpen: false,
    isRulesSaving: false,
    isDiscardRulesOpen: false,
    rulesError: ''
  };

  componentDidMount() {
    const { user, tournament } = this.props;
    document.addEventListener('mousedown', this.onDocumentMouseDown);
    document.addEventListener('keydown', this.onDocumentKeyDown);
    try {
      const storedRecipients = window.sessionStorage.getItem(
        `tournamentNotificationRecipients:${user.userId}:${tournament.id}`
      );
      const selectedSchools = storedRecipients ? JSON.parse(storedRecipients) : [];
      if (Array.isArray(selectedSchools) && selectedSchools.length) {
        this.setState({
          selectedSchools,
          selectedSchoolIds: selectedSchools.map(school => school.id)
        });
      }
    } catch (error) {
      // Keep the list usable if an old stored selection is invalid.
    }
    const savedColumns = window.localStorage.getItem(`unionTournamentEligibleColumns:${user.userId}`);
    if (savedColumns) {
      try {
        const visibleColumns = JSON.parse(savedColumns);
        if (Array.isArray(visibleColumns) && visibleColumns.includes('school')) this.setState({ visibleColumns });
      } catch (error) {
        // Ignore an invalid legacy preference and keep the safe defaults.
      }
    }
    const savedLogColumns = window.localStorage.getItem(`unionTournamentEnrollmentLogColumns:${user.userId}`);
    if (savedLogColumns) {
      try {
        const enrollmentLogVisibleColumns = JSON.parse(savedLogColumns);
        if (Array.isArray(enrollmentLogVisibleColumns)) this.setState({ enrollmentLogVisibleColumns });
      } catch (error) {
        // Ignore invalid stored preferences and keep the default columns.
      }
    }
    const savedAdminColumns = window.localStorage.getItem(`unionTournamentSchoolAdminColumns:${user.userId}`);
    if (savedAdminColumns) {
      try {
        const schoolAdminVisibleColumns = JSON.parse(savedAdminColumns);
        if (Array.isArray(schoolAdminVisibleColumns)) this.setState({ schoolAdminVisibleColumns });
      } catch (error) {
        // Ignore invalid stored preferences and keep the default columns.
      }
    }
    this.refreshCounts(false);
    this.loadActiveSection();
  }

  loadActiveSection = () => {
    if (['eligible', 'participants', 'waiting'].includes(this.props.activeSection) && this.state.areas.length === 0) {
      getAllSchoolUnionSchools(this.props.user, { kind: { $in: [KIND.SCHOOL_UNION] } })
        .then(areas => this.setState({ areas }))
        .catch(() => undefined);
    }
    switch (this.props.activeSection) {
      case 'eligible':
        this.loadEligibleSchools(this.state.eligiblePage, this.state.eligibleSearch);
        break;
      case 'participants':
        this.loadParticipants(this.state.participantsPage, this.state.participantsSearch);
        break;
      case 'schoolAdmins':
        this.loadSchoolAdmins();
        break;
      case 'waiting':
        this.loadWaitingList(this.state.waitingPage, this.state.waitingSearch);
        break;
      case 'requests':
        this.loadConfirmationRequests(
          this.state.confirmationPage,
          this.state.confirmationSearch,
          this.state.confirmationStatus
        );
        break;
      case 'log':
        this.loadEnrollmentLogs(
          this.state.enrollmentLogPage,
          this.state.enrollmentLogSearch,
          this.state.enrollmentLogAction,
          this.state.enrollmentLogDateRange
        );
        break;
      default:
        break;
    }
  };

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  onDocumentMouseDown = (event: MouseEvent) => {
    if (!this.state.isSchoolAdminColumnsOpen) return;
    const target = event.target as HTMLElement;
    if (!target.closest('.utmSchoolAdmins .gridModernColumns')) {
      this.setState({ isSchoolAdminColumnsOpen: false });
    }
  };

  onDocumentKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.state.isSchoolAdminColumnsOpen) {
      this.setState({ isSchoolAdminColumnsOpen: false });
    }
  };

  componentDidUpdate(prevProps: Props) {
    if (prevProps.user.userId !== this.props.user.userId) {
      this.setState({ confirmationVisibleColumns: loadConfirmationColumns(this.props.user.userId) });
    }
    if (prevProps.activeSection === this.props.activeSection) return;
    this.setState(
      {
        participantsPage: 1,
        participantsSearch: '',
        participantsSearchDraft: '',
        waitingPage: 1,
        waitingSearch: '',
        waitingSearchDraft: '',
        confirmationPage: 1,
        confirmationSearch: '',
        confirmationSearchDraft: '',
        confirmationStatus: '',
        confirmationStatusDraft: '',
        enrollmentLogPage: 1,
        enrollmentLogSearch: '',
        enrollmentLogSearchDraft: '',
        enrollmentLogAction: [],
        enrollmentLogActionDraft: [],
        enrollmentLogDateRange: { start: null, end: null },
        enrollmentLogDateRangeDraft: { start: null, end: null }
      },
      this.loadActiveSection
    );
  }

  createRulesDraft = (): RulesDraft => {
    const tournament = this.props.tournament;
    const existing = tournament.autoEnrollmentTeamsSettings || [];
    const ages = tournament.ages || [];
    const genders =
      tournament.gender === GENDER.MIXED
        ? tournament.isMixedTeamsAvailable
          ? [GENDER.MIXED]
          : [GENDER.MALE_ONLY, GENDER.FEMALE_ONLY]
        : [tournament.gender];
    const settings = ages.reduce((result, age) => {
      genders.forEach(gender => {
        const current = existing.find(item => item.age === age && item.gender === gender);
        result.push({ age, gender, maximumNumberTeams: Number(current?.maximumNumberTeams || 0) });
      });
      return result;
    }, [] as RulesDraft['settings']);
    return {
      isReadOnly: Boolean(tournament.isReadOnly),
      isAutoEnrollmentTeams: Boolean(tournament.isAutoEnrollmentTeams),
      isEnableWaitingListWhenFull: Boolean(tournament.isEnableWaitingListWhenFull),
      isAutoEnrollmentToWaitingList: Boolean(tournament.isAutoEnrollmentToWaitingList),
      maximumNumberTeamsOfSchool: isIndividualTournament(tournament)
        ? 1
        : Number(tournament.maximumNumberTeamsOfSchool || 0),
      individualCapacity: Number(existing[0]?.maximumNumberTeams || 0),
      settings
    };
  };

  openRulesEditor = () =>
    this.setState({
      isRulesEditorOpen: true,
      isDiscardRulesOpen: false,
      rulesDraft: this.createRulesDraft(),
      rulesError: ''
    });

  isRulesDraftDirty = () =>
    Boolean(this.state.rulesDraft && JSON.stringify(this.state.rulesDraft) !== JSON.stringify(this.createRulesDraft()));

  requestCloseRulesEditor = () => {
    if (this.state.isRulesSaving) return;
    if (this.isRulesDraftDirty()) {
      this.setState({ isDiscardRulesOpen: true });
      return;
    }
    this.setState({ isRulesEditorOpen: false, rulesDraft: undefined, rulesError: '' });
  };

  discardRulesChanges = () =>
    this.setState({
      isRulesEditorOpen: false,
      isDiscardRulesOpen: false,
      rulesDraft: undefined,
      rulesError: ''
    });

  updateRulesDraft = (patch: Partial<RulesDraft>) =>
    this.setState(state => ({ rulesDraft: state.rulesDraft ? { ...state.rulesDraft, ...patch } : state.rulesDraft }));

  saveRules = async () => {
    const draft = this.state.rulesDraft;
    if (!draft || this.state.isRulesSaving) return;
    if (draft.maximumNumberTeamsOfSchool < 0 || draft.maximumNumberTeamsOfSchool > 5) {
      this.setState({ rulesError: 'Maximum teams per school must be between 0 and 5.' });
      return;
    }
    const isIndividual = isIndividualTournament(this.props.tournament);
    const settings = isIndividual
      ? (this.props.tournament.ages || []).map((age, index) => ({
          age,
          gender: GENDER.MIXED,
          maximumNumberTeams: index === 0 ? Number(draft.individualCapacity || 0) : 0
        }))
      : draft.settings.map(item => ({ ...item, maximumNumberTeams: Number(item.maximumNumberTeams || 0) }));
    this.setState({ isRulesSaving: true, rulesError: '' });
    try {
      const updated = await updateTournamentAutoEnrollment(
        this.props.user,
        {
          autoEnrollmentTeamsSettings: settings,
          maximumNumberTeamsOfSchool: isIndividual ? 1 : Number(draft.maximumNumberTeamsOfSchool || 0),
          isReadOnly: draft.isReadOnly,
          isAutoEnrollmentTeams: draft.isAutoEnrollmentTeams,
          isEnableWaitingListWhenFull: draft.isAutoEnrollmentTeams && draft.isEnableWaitingListWhenFull,
          isAutoEnrollmentToWaitingList: draft.isAutoEnrollmentTeams && draft.isAutoEnrollmentToWaitingList
        },
        this.props.tournament.id
      );
      this.setState({ isRulesEditorOpen: false, isRulesSaving: false, rulesDraft: undefined });
      this.props.onUpdated({ ...this.props.tournament, ...updated });
      this.refreshCounts();
    } catch (error) {
      this.setState({
        isRulesSaving: false,
        rulesError: getRequestErrorMessage(error, 'Rules could not be saved. Please try again.')
      });
    }
  };

  getEligibleWhere = (search: string) => {
    const { filters } = this.state;
    const where: any = {};
    if (search) where.name = { like: search, options: 'i' };
    if (filters.ages.length) where.availableAges = { $in: filters.ages };
    if (filters.genders.length) where.availableGender = { $in: filters.genders };
    if (filters.participant !== '') where.isParticipant = filters.participant === 'true';
    if (filters.statuses.length === 1) where.status = filters.statuses[0];
    if (filters.statuses.length > 1) where.status = { $in: filters.statuses };
    if (filters.dateFrom || filters.dateTo) {
      where.statusSetAt = {};
      if (filters.dateFrom) {
        where.statusSetAt.$gte = moment(filters.dateFrom)
          .startOf('day')
          .toISOString();
      }
      if (filters.dateTo) {
        where.statusSetAt.$lte = moment(filters.dateTo)
          .endOf('day')
          .toISOString();
      }
    }
    if (filters.areas.length) where.areaSU = { $in: filters.areas };
    return where;
  };

  loadParticipants = (page: number, search: string) => {
    const { user, tournament } = this.props;
    const where: any = {};
    if (search) where.name = { like: search, options: 'i' };

    this.setState({ isParticipantsLoading: true });
    Promise.all([
      getTournamentParticipants(user, tournament.id, {
        where,
        limit: 20,
        skip: (page - 1) * 20,
        order: 'createdAt DESC'
      } as any),
      search
        ? safeCount(getTournamentParticipantsCount(user, tournament.id, where))
        : getTournamentManagementSummary(user, tournament.id).then(summary => summary.participants)
    ])
      .then(async ([participants, total]) => {
        const schoolIds = participants.map((participant: any) => String(participant.schoolId)).filter(Boolean);
        const entryLogs = schoolIds.length
          ? await getTournamentEnrollmentLogs(user, tournament.id, {
              where: {
                action: {
                  $in: [
                    TOURNAMENT_LOG_ACTION_TYPES.SCHOOL_ADDED,
                    TOURNAMENT_LOG_ACTION_TYPES.SCHOOL_AUTO_ENROLLED,
                    TOURNAMENT_LOG_ACTION_TYPES.SCHOOL_MANUALLY_ENROLLED
                  ]
                },
                $or: [
                  { 'affectedSchoolData.id': { $in: schoolIds } },
                  { 'inviteData.invitedSchoolId': { $in: schoolIds } }
                ]
              },
              limit: Math.max(20, schoolIds.length * 4),
              skip: 0,
              order: 'createdAt DESC'
            }).catch(() => [])
          : [];
        const participantTeamCounts: Record<string, number> = {};
        participants.forEach((participant: any) => {
          const participantSchoolId = String(participant.schoolId || participant.school?.id || '');
          if (participantSchoolId) participantTeamCounts[participantSchoolId] = Number(participant.teamsCount || 0);
        });
        const participantEntryMeta = entryLogs.reduce((meta, log: any) => {
          const logSchoolId = String(
            log.affectedSchoolData?.id ||
              log.affectedSchoolData?._id ||
              log.inviteData?.invitedSchoolId ||
              log.actionDetails?.schoolId ||
              ''
          );
          if (logSchoolId && !meta[logSchoolId]) meta[logSchoolId] = log;
          return meta;
        }, {});
        this.setState(state => ({
          participants,
          participantTeamCounts,
          participantEntryMeta,
          participantsPage: page,
          participantsSearch: search,
          participantsUnfilteredTotal: search ? state.participantsUnfilteredTotal : total,
          counts: { ...state.counts, participants: total },
          isParticipantsLoading: false
        }));
      })
      .catch(() => this.setState({ participants: [], isParticipantsLoading: false }));
  };

  onParticipantsSearch = (event: React.FormEvent) => {
    event.preventDefault();
    this.loadParticipants(1, this.state.participantsSearchDraft.trim());
  };

  getSchoolIdsMatchingName = (text: string): Promise<string[]> => {
    const query = text.trim();
    if (!query) return Promise.resolve([]);

    const tournamentSchoolIds = new Set((this.props.tournament.schoolIds || []).map(item => String(item.schoolId)));
    return getAllSchoolUnionSchools(this.props.user, { name: { like: query, options: 'i' } })
      .then(schools =>
        schools.map(school => String(school.id || school._id || '')).filter(id => id && tournamentSchoolIds.has(id))
      )
      .catch(() => []);
  };

  getSchoolAdminWhere = async (search: string, filters: State['schoolAdminFilters']): Promise<any> => {
    const query = search.trim();
    const schoolQuery = String(filters.schools[0] || '').trim();
    const [searchSchoolIds, filterSchoolIds] = await Promise.all([
      this.getSchoolIdsMatchingName(query),
      schoolQuery === query ? this.getSchoolIdsMatchingName(query) : this.getSchoolIdsMatchingName(schoolQuery)
    ]);
    const conditions: any[] = [];

    if (query) {
      const like = { like: query, options: 'i' };
      const searchConditions: any[] = [{ firstName: like }, { lastName: like }, { email: like }, { phone: like }];
      if (searchSchoolIds.length) {
        searchConditions.push({ permissions: { $elemMatch: { schoolId: { $in: searchSchoolIds } } } });
      }
      conditions.push({ $or: searchConditions });
    }
    if (schoolQuery) {
      conditions.push(
        filterSchoolIds.length
          ? { permissions: { $elemMatch: { schoolId: { $in: filterSchoolIds } } } }
          : { _id: { $in: [] } }
      );
    }
    if (filters.contacts.includes('email')) {
      conditions.push({ email: { $exists: true, $nin: ['', null] } });
    }
    if (filters.contacts.includes('phone')) {
      conditions.push({ phone: { $exists: true, $nin: ['', null] } });
    }

    if (conditions.length === 0) return {};
    if (conditions.length === 1) return conditions[0];
    return { $and: conditions };
  };

  loadSchoolAdmins = async (
    page = 1,
    search = this.state.schoolAdminsSearch,
    filters = this.state.schoolAdminFilters
  ) => {
    const { user, tournament } = this.props;
    this.setState({
      isSchoolAdminsLoading: true,
      schoolAdminsPage: page,
      schoolAdminsSearch: search,
      schoolAdminFilters: filters
    });

    try {
      const where = await this.getSchoolAdminWhere(search, filters);
      const [schoolAdmins, total] = await Promise.all([
        getTournamentAdmins(user, tournament.id, {
          where,
          limit: 20,
          skip: (page - 1) * 20,
          order: 'lastName ASC'
        }),
        safeCount(getTournamentAdminsCount(user, tournament.id, where))
      ]);
      this.setState(state => ({
        schoolAdmins,
        isSchoolAdminsLoading: false,
        counts: { ...state.counts, schoolAdmins: total }
      }));
    } catch (error) {
      this.setState(state => ({
        schoolAdmins: [],
        isSchoolAdminsLoading: false,
        counts: { ...state.counts, schoolAdmins: 0 }
      }));
    }
  };

  onSchoolAdminsSearch = (event: React.FormEvent) => {
    event.preventDefault();
    this.loadSchoolAdmins(1, this.state.schoolAdminsSearchDraft.trim(), this.state.schoolAdminFilters);
  };

  getSchoolAdminSchools = (admin: any): string[] =>
    Array.from(
      new Set<string>(
        (admin.permissions || []).map(permission => permission.school?.name || permission.schoolName).filter(Boolean)
      )
    );

  getSchoolAdminId = (admin: any): string => String(admin.id || admin._id || '');

  toggleSchoolAdmin = (admin: any) => {
    const id = this.getSchoolAdminId(admin);
    if (!id) return;
    this.setState(state => ({
      selectedSchoolAdminIds: state.selectedSchoolAdminIds.includes(id)
        ? state.selectedSchoolAdminIds.filter(value => value !== id)
        : [...state.selectedSchoolAdminIds, id]
    }));
  };

  toggleSchoolAdminColumn = (column: string) => {
    this.setState(
      state => ({
        schoolAdminVisibleColumns: state.schoolAdminVisibleColumns.includes(column)
          ? state.schoolAdminVisibleColumns.filter(value => value !== column)
          : [...state.schoolAdminVisibleColumns, column]
      }),
      () =>
        window.localStorage.setItem(
          `unionTournamentSchoolAdminColumns:${this.props.user.userId}`,
          JSON.stringify(this.state.schoolAdminVisibleColumns)
        )
    );
  };

  resetSchoolAdminColumns = () => {
    const schoolAdminVisibleColumns = ['email', 'phone', 'school'];
    this.setState({ schoolAdminVisibleColumns });
    window.localStorage.setItem(
      `unionTournamentSchoolAdminColumns:${this.props.user.userId}`,
      JSON.stringify(schoolAdminVisibleColumns)
    );
  };

  toggleSchoolAdminFilter = (type: 'schools' | 'contacts', value: string) => {
    this.setState(state => ({
      schoolAdminFilterDraft: {
        ...state.schoolAdminFilterDraft,
        [type]: state.schoolAdminFilterDraft[type].includes(value)
          ? state.schoolAdminFilterDraft[type].filter(item => item !== value)
          : [...state.schoolAdminFilterDraft[type], value]
      }
    }));
  };

  renderSchoolAdmins() {
    const {
      isSchoolAdminsLoading,
      schoolAdminsSearch,
      schoolAdminsSearchDraft,
      schoolAdminsPage,
      selectedSchoolAdminIds,
      schoolAdminFilters,
      schoolAdminFilterDraft,
      schoolAdminVisibleColumns: visible
    } = this.state;
    const limit = 20;
    const pages = Math.max(1, Math.ceil(this.state.counts.schoolAdmins / limit));
    const currentPage = Math.min(schoolAdminsPage, pages);
    const admins = this.state.schoolAdmins;
    const pageIds = admins.map(this.getSchoolAdminId).filter(Boolean);
    const isPageSelected = pageIds.length > 0 && pageIds.every(id => selectedSchoolAdminIds.includes(id));
    const activeFilterCount = schoolAdminFilters.schools.length + schoolAdminFilters.contacts.length;
    const columnCount = 2 + visible.length;

    return (
      <div className="utmEntries__content">
        <article className="utmEntries__panel utmSchoolAdmins unionTournamentsModern">
          <header>
            <div>
              <h2>
                School admins <span className="utmEntries__countChip">{this.state.counts.schoolAdmins}</span>
              </h2>
            </div>
            <div className="utmSchoolAdmins__headerTools">
              <div className="gridModernSearch utmSchoolAdmins__search">
                <form onSubmit={this.onSchoolAdminsSearch}>
                  <input
                    value={schoolAdminsSearchDraft}
                    onChange={event => this.setState({ schoolAdminsSearchDraft: event.target.value })}
                    placeholder="Search by name, email, phone or school"
                  />
                </form>
                {schoolAdminsSearchDraft ? (
                  <button
                    className="gridModernSearch__clear"
                    type="button"
                    aria-label="Clear search"
                    onClick={() =>
                      this.setState({ schoolAdminsSearchDraft: '' }, () =>
                        this.loadSchoolAdmins(1, '', this.state.schoolAdminFilters)
                      )
                    }
                  >
                    ×
                  </button>
                ) : null}
              </div>
              <div className="gridModernFiltersAnchor">
                <button
                  type="button"
                  className={`gridModernFilterButton ${activeFilterCount ? 'is-active' : ''}`}
                  onClick={() =>
                    this.setState({
                      isSchoolAdminFiltersOpen: true,
                      schoolAdminFilterDraft: {
                        schools: [...schoolAdminFilters.schools],
                        contacts: [...schoolAdminFilters.contacts]
                      }
                    })
                  }
                >
                  <span aria-hidden="true" /> Filters {activeFilterCount ? <b>{activeFilterCount}</b> : null}
                </button>
              </div>
              <div className="gridModernColumns">
                <button
                  type="button"
                  onClick={() =>
                    this.setState(state => ({ isSchoolAdminColumnsOpen: !state.isSchoolAdminColumnsOpen }))
                  }
                >
                  <span aria-hidden="true" /> Columns <b>{columnCount}</b>
                </button>
                {this.state.isSchoolAdminColumnsOpen ? (
                  <div className="gridModernColumns__menu">
                    <header>
                      <span>Table columns</span>
                      <button type="button" onClick={this.resetSchoolAdminColumns}>
                        Reset
                      </button>
                    </header>
                    <label>
                      <input type="checkbox" checked disabled /> <span>Name</span>
                    </label>
                    {[
                      ['email', 'Email'],
                      ['phone', 'Phone'],
                      ['school', 'School']
                    ].map(([key, label]) => (
                      <label key={key}>
                        <input
                          type="checkbox"
                          checked={visible.includes(key)}
                          onChange={() => this.toggleSchoolAdminColumn(key)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </header>

          {selectedSchoolAdminIds.length ? (
            <div className="utmEligible__selection utmSchoolAdmins__selection">
              <strong>Selected {selectedSchoolAdminIds.length}</strong>
              <button type="button" className="utmSchoolAdmins__download" onClick={this.downloadSchoolAdminsCsv}>
                <Icon path="M12 3v12M7 10l5 5 5-5M5 21h14" /> Download selected CSV
              </button>
              <button
                className="is-clearSelection"
                type="button"
                onClick={() => this.setState({ selectedSchoolAdminIds: [] })}
              >
                <span aria-hidden="true">×</span> Clear selection
              </button>
            </div>
          ) : null}

          {schoolAdminsSearch || schoolAdminFilters.schools.length || schoolAdminFilters.contacts.length ? (
            <div className="utmEligible__criteria">
              {schoolAdminsSearch ? (
                <span>
                  Search: {schoolAdminsSearch}
                  <button
                    type="button"
                    aria-label="Clear school admin search"
                    onClick={() => this.setState({ schoolAdminsSearch: '', schoolAdminsSearchDraft: '' })}
                  >
                    ×
                  </button>
                </span>
              ) : null}
              {schoolAdminFilters.schools.map(school => (
                <span key={school}>
                  School contains: {school}
                  <button
                    type="button"
                    aria-label={`Remove ${school} filter`}
                    onClick={() =>
                      this.loadSchoolAdmins(1, schoolAdminsSearch, {
                        ...schoolAdminFilters,
                        schools: schoolAdminFilters.schools.filter(value => value !== school)
                      })
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
              {schoolAdminFilters.contacts.map(contact => (
                <span key={contact}>
                  Has {contact}
                  <button
                    type="button"
                    aria-label={`Remove has ${contact} filter`}
                    onClick={() =>
                      this.loadSchoolAdmins(1, schoolAdminsSearch, {
                        ...schoolAdminFilters,
                        contacts: schoolAdminFilters.contacts.filter(value => value !== contact)
                      })
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : null}

          <div className="utmParticipants__tableWrap">
            <table>
              <thead>
                <tr>
                  <th className="utmSchoolAdmins__selectColumn">
                    <SelectAllCheckbox
                      compact
                      isChecked={isPageSelected}
                      totalCount={this.state.counts.schoolAdmins}
                      currentPage={currentPage}
                      pageItemsCount={admins.length}
                      selectedItems={selectedSchoolAdminIds}
                      selectedItemsCount={selectedSchoolAdminIds.length}
                      onSelectAllOnPageClick={() =>
                        this.setState(state => ({
                          selectedSchoolAdminIds: Array.from(new Set([...state.selectedSchoolAdminIds, ...pageIds]))
                        }))
                      }
                      onSelectAllClick={this.selectAllFilteredSchoolAdmins}
                      onUnselectAllClick={() => this.setState({ selectedSchoolAdminIds: [] })}
                    />
                  </th>
                  <th>Name</th>
                  {visible.includes('email') ? <th>Email</th> : null}
                  {visible.includes('phone') ? <th>Phone</th> : null}
                  {visible.includes('school') ? <th>School</th> : null}
                </tr>
              </thead>
              <tbody>
                {isSchoolAdminsLoading ? (
                  <tr>
                    <td colSpan={columnCount} className="utmEligible__empty">
                      Loading school admins…
                    </td>
                  </tr>
                ) : null}
                {!isSchoolAdminsLoading &&
                  admins.map(admin => (
                    <tr key={this.getSchoolAdminId(admin)}>
                      <td className="utmSchoolAdmins__selectColumn">
                        <input
                          type="checkbox"
                          checked={selectedSchoolAdminIds.includes(this.getSchoolAdminId(admin))}
                          onChange={() => this.toggleSchoolAdmin(admin)}
                          aria-label={`Select ${[admin.firstName, admin.lastName].filter(Boolean).join(' ') ||
                            'admin'}`}
                        />
                      </td>
                      <td>
                        <strong>{[admin.firstName, admin.lastName].filter(Boolean).join(' ') || '—'}</strong>
                      </td>
                      {visible.includes('email') ? <td>{admin.email || '—'}</td> : null}
                      {visible.includes('phone') ? <td>{this.getSchoolAdminPhone(admin) || '—'}</td> : null}
                      {visible.includes('school') ? (
                        <td>{this.getSchoolAdminSchools(admin).join(', ') || '—'}</td>
                      ) : null}
                    </tr>
                  ))}
                {!isSchoolAdminsLoading && admins.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} className="utmEligible__empty">
                      <TableEmptyState
                        filtered={Boolean(schoolAdminsSearch || schoolAdminFilters.schools.length || schoolAdminFilters.contacts.length)}
                        emptyTitle="No school admins yet"
                        emptyText="School administrators connected to participating schools will appear here."
                        filteredTitle="No school admins match your search"
                        filteredText="Try changing the search term or clearing the selected filters."
                        icon="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M19 8v6M22 11h-6"
                      />
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <Pagination2
            modern
            currentPage={currentPage}
            total={this.state.counts.schoolAdmins}
            limit={limit}
            setCurrentPageParams={page => this.loadSchoolAdmins(page, schoolAdminsSearch, schoolAdminFilters)}
          />
        </article>

        {this.state.isSchoolAdminFiltersOpen ? (
          <div
            className="utmSchoolAdmins__filterBackdrop unionTournamentsModern"
            onMouseDown={() => this.setState({ isSchoolAdminFiltersOpen: false })}
          >
            <form
              className="gridModernFilterPopover"
              onMouseDown={event => event.stopPropagation()}
              onSubmit={event => {
                event.preventDefault();
                this.setState({ isSchoolAdminFiltersOpen: false });
                this.loadSchoolAdmins(1, schoolAdminsSearch, schoolAdminFilterDraft);
              }}
            >
              <header>
                <strong>Filters</strong>
                <button type="button" onClick={() => this.setState({ isSchoolAdminFiltersOpen: false })}>
                  ×
                </button>
              </header>
              <div className="gridModernFilterPopover__fields">
                <label>
                  <span>School</span>
                  <input
                    type="text"
                    value={schoolAdminFilterDraft.schools[0] || ''}
                    placeholder="Enter part of a school name"
                    onChange={event =>
                      this.setState({
                        schoolAdminFilterDraft: {
                          ...schoolAdminFilterDraft,
                          schools: event.target.value ? [event.target.value] : []
                        }
                      })
                    }
                  />
                </label>
                <fieldset className="gridModernFilterChips">
                  <legend>Contact details</legend>
                  <div>
                    {[
                      ['email', 'Has email'],
                      ['phone', 'Has phone']
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        className={schoolAdminFilterDraft.contacts.includes(value) ? 'is-selected' : ''}
                        onClick={() => this.toggleSchoolAdminFilter('contacts', value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </fieldset>
              </div>
              <footer>
                <button
                  type="button"
                  className="is-clear"
                  onClick={() => this.setState({ schoolAdminFilterDraft: { schools: [], contacts: [] } })}
                >
                  Clear all
                </button>
                <div>
                  <button type="button" onClick={() => this.setState({ isSchoolAdminFiltersOpen: false })}>
                    Cancel
                  </button>
                  <button type="submit" className="is-apply">
                    Apply
                  </button>
                </div>
              </footer>
            </form>
          </div>
        ) : null}
      </div>
    );
  }

  getSchoolAdminPhone = (admin: any): string =>
    admin.phone || admin.mobilePhone || admin.profile?.phone || (admin.phones || []).join(', ') || '';

  selectAllFilteredSchoolAdmins = async () => {
    const where = await this.getSchoolAdminWhere(this.state.schoolAdminsSearch, this.state.schoolAdminFilters);
    const admins = await getAllTournamentAdmins(this.props.user, this.props.tournament.id, where).catch(() => []);
    this.setState({ selectedSchoolAdminIds: admins.map(this.getSchoolAdminId).filter(Boolean) });
  };

  downloadSchoolAdminsCsv = async () => {
    const csvCell = (value: any) => `"${String(value || '').replace(/"/g, '""')}"`;
    const selectedIds = this.state.selectedSchoolAdminIds;
    if (!selectedIds.length) return;
    const where = await this.getSchoolAdminWhere(this.state.schoolAdminsSearch, this.state.schoolAdminFilters);
    const filteredAdmins = await getAllTournamentAdmins(
      this.props.user,
      this.props.tournament.id,
      where
    ).catch(() => []);
    const rows = filteredAdmins
      .filter(admin => selectedIds.includes(this.getSchoolAdminId(admin)))
      .map(admin => [
        admin.firstName,
        admin.lastName,
        admin.email,
        this.getSchoolAdminPhone(admin),
        this.getSchoolAdminSchools(admin).join(', ')
      ]);
    const csv = [['First name', 'Last name', 'Email', 'Phone', 'Schools'], ...rows]
      .map(row => row.map(csvCell).join(','))
      .join('\r\n');
    const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${String(this.props.tournament.name || 'tournament').replace(
      /[^a-z0-9]+/gi,
      '-'
    )}-school-admins.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  getEnrollmentLogWhere = (search: string, action: string[], dateRange: DateRange) => {
    const where: any = {};
    if (search) {
      const schoolName = { like: search, options: 'i' };
      where.$or = [{ 'affectedSchoolData.name': schoolName }, { 'inviteData.invitedSchoolName': schoolName }];
    }
    if (action.length === 1) where.action = action[0];
    if (action.length > 1) where.action = { $in: action };
    if (dateRange.start || dateRange.end) {
      where.createdAt = {};
      if (dateRange.start) {
        where.createdAt.$gte = moment(dateRange.start)
          .startOf('day')
          .toISOString();
      }
      if (dateRange.end) {
        where.createdAt.$lte = moment(dateRange.end)
          .endOf('day')
          .toISOString();
      }
    }
    return where;
  };

  loadEnrollmentLogs = (page: number, search: string, action: string[], dateRange: DateRange) => {
    const { user, tournament } = this.props;
    const where = this.getEnrollmentLogWhere(search, action, dateRange);
    this.setState({ isEnrollmentLogLoading: true });
    Promise.all([
      getTournamentEnrollmentLogs(user, tournament.id, {
        where,
        limit: 20,
        skip: (page - 1) * 20,
        order: 'createdAt DESC'
      }),
      search || action.length || dateRange.start || dateRange.end
        ? safeCount(getTournamentEnrollmentLogsCount(user, tournament.id, where))
        : getTournamentManagementSummary(user, tournament.id).then(summary => summary.logs)
    ])
      .then(([enrollmentLogs, total]) =>
        this.setState(state => ({
          enrollmentLogs,
          enrollmentLogPage: page,
          enrollmentLogSearch: search,
          enrollmentLogAction: action,
          enrollmentLogDateRange: dateRange,
          counts: { ...state.counts, log: total },
          isEnrollmentLogLoading: false
        }))
      )
      .catch(() => this.setState({ enrollmentLogs: [], isEnrollmentLogLoading: false }));
  };

  onEnrollmentLogSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const { enrollmentLogSearchDraft, enrollmentLogAction, enrollmentLogDateRange } = this.state;
    this.loadEnrollmentLogs(1, enrollmentLogSearchDraft.trim(), enrollmentLogAction, enrollmentLogDateRange);
  };

  loadWaitingList = (page: number, search: string) => {
    const { user, tournament } = this.props;
    const where: any = {};
    if (search) where.name = { like: search, options: 'i' };
    this.setState({ isWaitingLoading: true });
    Promise.all([
      getTournamentWaitingList(user, tournament.id, {
        where,
        limit: 20,
        skip: (page - 1) * 20
      } as any),
      search
        ? safeCount(getTournamentWaitingListCount(user, tournament.id, where))
        : getTournamentManagementSummary(user, tournament.id).then(summary => summary.waiting)
    ])
      .then(async ([waitingItems, total]) => {
        const waitingSchoolIds = waitingItems
          .map((item: any) => String(item.schoolId || item.id || ''))
          .filter(Boolean);
        const waitingLogs = waitingSchoolIds.length
          ? await getTournamentEnrollmentLogs(user, tournament.id, {
              where: {
                action: {
                  $in: [
                    TOURNAMENT_LOG_ACTION_TYPES.SCHOOL_ADDED_TO_WAITING_LIST,
                    TOURNAMENT_LOG_ACTION_TYPES.INVITE_ACCEPTED
                  ]
                },
                $or: [
                  { 'affectedSchoolData.id': { $in: waitingSchoolIds } },
                  { 'inviteData.invitedSchoolId': { $in: waitingSchoolIds } }
                ]
              },
              limit: Math.max(20, waitingSchoolIds.length * 4),
              skip: 0,
              order: 'createdAt DESC'
            }).catch(() => [])
          : [];
        const waitingEntryMeta = waitingLogs.reduce((meta, log: any) => {
          const logSchoolId = String(
            log.affectedSchoolData?.id ||
              log.affectedSchoolData?._id ||
              log.inviteData?.invitedSchoolId ||
              log.actionDetails?.schoolId ||
              ''
          );
          if (logSchoolId && (!meta[logSchoolId] || log.action === TOURNAMENT_LOG_ACTION_TYPES.INVITE_ACCEPTED)) {
            meta[logSchoolId] = log;
          }
          return meta;
        }, {});
        this.setState(state => ({
          waitingItems,
          waitingEntryMeta,
          waitingPage: page,
          waitingSearch: search,
          counts: { ...state.counts, waiting: total },
          isWaitingLoading: false
        }));
      })
      .catch(() => this.setState({ waitingItems: [], isWaitingLoading: false }));
  };

  onWaitingSearch = (event: React.FormEvent) => {
    event.preventDefault();
    this.loadWaitingList(1, this.state.waitingSearchDraft.trim());
  };

  loadConfirmationRequests = (page: number, search: string, status: string) => {
    const { user, tournament } = this.props;
    const where: any = {};
    if (search) where.participantSchoolName = { like: search, options: 'i' };
    if (status) where.invitationStatus = status;
    this.setState({ isConfirmationLoading: true });
    Promise.all([
      getTournamentConfirmationRequests(user, tournament.id, {
        where,
        limit: 20,
        skip: (page - 1) * 20,
        order: 'createdAt DESC'
      } as any),
      search || status
        ? safeCount(getTournamentConfirmationRequestsCount(user, tournament.id, where))
        : getTournamentManagementSummary(user, tournament.id).then(summary => summary.confirmationTotal)
    ])
      .then(([confirmationRequests, total]) =>
        this.setState(state => ({
          confirmationRequests,
          confirmationPage: page,
          confirmationSearch: search,
          confirmationStatus: status,
          counts: { ...state.counts, requests: total },
          isConfirmationLoading: false
        }))
      )
      .catch(() => this.setState({ confirmationRequests: [], isConfirmationLoading: false }));
  };

  onConfirmationSearch = (event: React.FormEvent) => {
    event.preventDefault();
    this.loadConfirmationRequests(1, this.state.confirmationSearchDraft.trim(), this.state.confirmationStatus);
  };

  openSendConfirmationRequests = async () => {
    this.setState({
      isSendRequestsOpen: true,
      confirmationSendError: '',
      confirmationSendStep: 'form',
      preparedConfirmationFields: [],
      preparedConfirmationFormDefinition: undefined,
      confirmationDeadline: null
    });
    try {
      const [confirmationRecipients, confirmationTemplate, existingRequests] = await Promise.all([
        getAllTournamentParticipants(this.props.user, this.props.tournament.id),
        getConfirmationRequestTemplate(this.props.user),
        getAllTournamentConfirmationRequests(this.props.user, this.props.tournament.id)
      ]);
      const latestBySchool = existingRequests.reduce((result, request: any) => {
        const schoolId = String(request.participantSchoolId || request.participantSchoolData?.id || '');
        if (schoolId && (!result[schoolId] || moment(request.createdAt).isAfter(moment(result[schoolId].createdAt)))) {
          result[schoolId] = request;
        }
        return result;
      }, {});
      const sendable = confirmationRecipients.filter((recipient: any) => {
        const latest = latestBySchool[String(recipient.schoolId)];
        if (!latest) return true;
        return (
          latest.invitationStatus === CONFIRMATION_REQUESTS_STATUS.NOT_READY ||
          latest.invitationStatus === CONFIRMATION_REQUESTS_STATUS.OUTDATED
        );
      });
      this.setState({
        confirmationRecipients: sendable,
        confirmationTemplate,
        selectedConfirmationRecipientIds: sendable.map((recipient: any) => String(recipient.schoolId))
      });
    } catch (error) {
      this.setState({ confirmationSendError: 'Unable to load participants and confirmation request template.' });
    }
  };

  prepareConfirmationRequest = (fields: any[], deadline?: string, formDefinition?: any) => {
    const parsedDeadline = deadline ? moment(deadline, INPUT_MASK_VALIDATION, true) : null;
    this.setState({
      preparedConfirmationFields: fields,
      preparedConfirmationFormDefinition: formDefinition,
      confirmationDeadline: parsedDeadline && parsedDeadline.isValid() ? parsedDeadline.toDate() : null,
      confirmationSendStep: 'recipients',
      confirmationSendError: ''
    });
  };

  requestCloseConfirmationSend = () => {
    if (this.state.isSendingConfirmationRequests) return;
    if (this.state.confirmationSendStep === 'recipients') {
      this.setState({ isDiscardConfirmationSendOpen: true });
      return;
    }
    this.setState({ isSendRequestsOpen: false });
  };

  discardConfirmationSend = () => {
    this.setState({
      isSendRequestsOpen: false,
      isDiscardConfirmationSendOpen: false,
      confirmationSendStep: 'form',
      preparedConfirmationFields: [],
      preparedConfirmationFormDefinition: undefined,
      confirmationDeadline: null
    });
  };

  sendNewConfirmationRequests = async () => {
    const {
      confirmationTemplate,
      selectedConfirmationRecipientIds,
      confirmationDeadline,
      preparedConfirmationFields,
      preparedConfirmationFormDefinition
    } = this.state;
    if (!selectedConfirmationRecipientIds.length || !confirmationTemplate || this.state.isSendingConfirmationRequests) {
      return;
    }
    this.setState({ isSendingConfirmationRequests: true, confirmationSendError: '' });
    try {
      const formDefinition = preparedConfirmationFormDefinition || confirmationTemplate.formDefinition;
      const fields = preparedConfirmationFields.length
        ? preparedConfirmationFields
        : formDefinition
        ? formDefinitionToLegacyFields(formDefinition)
        : confirmationTemplate.fields || [];
      await Promise.all(
        selectedConfirmationRecipientIds.map(participantSchoolId =>
          sendConfirmationRequest(this.props.user, this.props.tournament.id, {
            participantSchoolId,
            fields,
            ...(formDefinition ? { formSchemaVersion: formDefinition.schemaVersion || 2, formDefinition } : {}),
            deadlineForAnswers: confirmationDeadline ? confirmationDeadline.toISOString() : null
          })
        )
      );
      this.setState({
        isSendRequestsOpen: false,
        isSendingConfirmationRequests: false,
        confirmationDeadline: null,
        selectedConfirmationRecipientIds: []
      });
      this.loadConfirmationRequests(1, '', '');
    } catch (error) {
      this.setState({
        isSendingConfirmationRequests: false,
        confirmationSendError:
          'Some confirmation requests could not be sent. Please review the recipients and try again.'
      });
    }
  };

  loadEligibleSchools = (page: number, search: string) => {
    const { user, tournament } = this.props;
    const where = this.getEligibleWhere(search);
    this.setState({ isEligibleLoading: true });
    Promise.all([
      getTournamentEligibleSchools(user, tournament.id, { where, limit: 20, skip: (page - 1) * 20, order: 'name ASC' }),
      Object.keys(where).length
        ? safeCount(getTournamentEligibleSchoolsCount(user, tournament.id, where))
        : getTournamentManagementSummary(user, tournament.id).then(summary => summary.eligible)
    ]).then(([eligibleSchools, eligible]) => {
      this.setState(state => ({
        eligibleSchools,
        eligiblePage: page,
        eligibleSearch: search,
        counts: { ...state.counts, eligible },
        isEligibleLoading: false
      }));
    });
  };

  onEligibleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    this.loadEligibleSchools(1, this.state.eligibleSearchDraft.trim());
  };

  toggleSchool = (school: EligibleSchool) => {
    const schoolId = school.id;
    this.setState(state => ({
      selectedSchoolIds: state.selectedSchoolIds.includes(schoolId)
        ? state.selectedSchoolIds.filter(id => id !== schoolId)
        : [...state.selectedSchoolIds, schoolId],
      selectedSchools: state.selectedSchoolIds.includes(schoolId)
        ? state.selectedSchools.filter(item => item.id !== schoolId)
        : [...state.selectedSchools, school]
    }));
  };

  toggleEligiblePage = () => {
    const pageIds = this.state.eligibleSchools.map(school => school.id);
    this.setState(state => ({
      selectedSchoolIds: Array.from(new Set([...state.selectedSchoolIds, ...pageIds])),
      selectedSchools: [
        ...state.selectedSchools.filter(school => !pageIds.includes(school.id)),
        ...state.eligibleSchools
      ]
    }));
  };

  selectAllEligibleSchools = () => {
    getAllTournamentEligibleSchools(
      this.props.user,
      this.props.tournament.id,
      this.getEligibleWhere(this.state.eligibleSearch)
    ).then((schools: EligibleSchool[]) =>
      this.setState({
        selectedSchoolIds: schools.map(school => school.id),
        selectedSchools: schools,
        isSelectMenuOpen: false
      })
    );
  };

  toggleFilterValue = (field: 'ages' | 'genders' | 'statuses' | 'areas', value: any) => {
    this.setState(state => {
      const values: any[] = state.eligibleFilterDraft[field] as any[];
      return {
        eligibleFilterDraft: {
          ...state.eligibleFilterDraft,
          [field]: values.includes(value) ? values.filter(item => item !== value) : [...values, value]
        }
      } as any;
    });
  };

  getAppliedFiltersCount = () => {
    const { filters } = this.state;
    return [
      filters.ages.length,
      filters.genders.length,
      filters.participant !== '',
      filters.statuses.length,
      filters.dateFrom || filters.dateTo,
      filters.areas.length
    ].filter(Boolean).length;
  };

  applyEligibleFilters = (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    this.setState(
      state => ({ filters: { ...state.eligibleFilterDraft }, isFiltersOpen: false }),
      () => this.loadEligibleSchools(1, this.state.eligibleSearch)
    );
  };

  onEligibleFiltersKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Enter') return;
    const target = event.target as HTMLElement;
    if (target.closest('.sit-dp')) return;
    event.preventDefault();
    this.applyEligibleFilters();
  };

  getSchoolAreas = (school: EligibleSchool) =>
    this.state.areas
      .filter(area => (area.schoolIds || []).includes(school.id))
      .map(area => area.name)
      .join(', ');

  toggleEligibleColumn = (column: string) => {
    this.setState(state => {
      const visibleColumns = state.visibleColumns.includes(column)
        ? state.visibleColumns.filter(field => field !== column)
        : [...state.visibleColumns, column];
      window.localStorage.setItem(
        `unionTournamentEligibleColumns:${this.props.user.userId}`,
        JSON.stringify(visibleColumns)
      );
      return { visibleColumns };
    });
  };

  resetEligibleColumns = () => {
    const visibleColumns = [...DEFAULT_ELIGIBLE_COLUMNS];
    window.localStorage.setItem(
      `unionTournamentEligibleColumns:${this.props.user.userId}`,
      JSON.stringify(visibleColumns)
    );
    this.setState({ visibleColumns });
  };

  toggleEnrollmentLogColumn = (column: string) => {
    this.setState(state => {
      const enrollmentLogVisibleColumns = state.enrollmentLogVisibleColumns.includes(column)
        ? state.enrollmentLogVisibleColumns.filter(field => field !== column)
        : [...state.enrollmentLogVisibleColumns, column];
      window.localStorage.setItem(
        `unionTournamentEnrollmentLogColumns:${this.props.user.userId}`,
        JSON.stringify(enrollmentLogVisibleColumns)
      );
      return { enrollmentLogVisibleColumns };
    });
  };

  resetEnrollmentLogColumns = () => {
    const enrollmentLogVisibleColumns = [...DEFAULT_ENROLLMENT_LOG_COLUMNS];
    window.localStorage.setItem(
      `unionTournamentEnrollmentLogColumns:${this.props.user.userId}`,
      JSON.stringify(enrollmentLogVisibleColumns)
    );
    this.setState({ enrollmentLogVisibleColumns });
  };

  toggleConfirmationColumn = (column: string) => {
    this.setState(state => {
      const confirmationVisibleColumns = state.confirmationVisibleColumns.includes(column)
        ? state.confirmationVisibleColumns.filter(field => field !== column)
        : [...state.confirmationVisibleColumns, column];
      window.localStorage.setItem(
        confirmationColumnsStorageKey(this.props.user.userId),
        JSON.stringify(confirmationVisibleColumns)
      );
      return { confirmationVisibleColumns };
    });
  };

  resetConfirmationColumns = () => {
    const confirmationVisibleColumns = [...DEFAULT_CONFIRMATION_COLUMNS];
    window.localStorage.setItem(
      confirmationColumnsStorageKey(this.props.user.userId),
      JSON.stringify(confirmationVisibleColumns)
    );
    this.setState({ confirmationVisibleColumns });
  };

  clearEligibleFilter = (field: keyof State['filters']) => {
    const emptyValue = ['ages', 'genders', 'statuses', 'areas'].includes(field) ? [] : '';
    this.setState(
      state => ({ filters: { ...state.filters, [field]: emptyValue } } as any),
      () => this.loadEligibleSchools(1, this.state.eligibleSearch)
    );
  };

  openRemoveConfirmation = () => this.setState({ isRemoveConfirmationOpen: true, removeParticipantsError: '' });

  openAddConfirmation = () => {
    this.setState({ isAddConfirmationOpen: true, addParticipantsError: '', participantEventSportSearch: '' });
    if (!isIndividualTournament(this.props.tournament)) return;
    this.setState({ isParticipantEventsLoading: true });
    getTournamentEvents(this.props.user, this.props.tournament.id, {
      where: {}, skip: 0, limit: 3000, order: 'startTime ASC'
    }, { summary: true })
      .then((events: any[]) => this.setState({
        participantEvents: events,
        selectedParticipantEventIds: events.map(event => event.id),
        isParticipantEventsLoading: false
      }))
      .catch(() => this.setState({
        participantEvents: [],
        selectedParticipantEventIds: [],
        isParticipantEventsLoading: false,
        addParticipantsError: 'Tournament events could not be loaded. You can retry by reopening this window.'
      }));
  };

  getParticipantEventName = (event: any): string => {
    const generatedNames = event.generatedNames || {};
    const fallbackName = [
      event.sport?.name,
      formatTeamAgesCompact(event.ages, 'ENGLISH'),
      TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[event.gender] || event.gender
    ].filter(Boolean).join(' · ');
    return String(
      event.eventDetails?.shortDescription ||
        event.shortDescription ||
        fallbackName ||
        generatedNames[this.props.user.activeSchoolId] ||
        generatedNames.official ||
        event.generatedName ||
        Object.keys(generatedNames).map(key => generatedNames[key]).find(Boolean) ||
        'Tournament event'
    );
  };

  getVisibleParticipantEvents = (): any[] => {
    const search = this.state.participantEventSportSearch.trim().toLowerCase();
    if (!search) return this.state.participantEvents;
    return this.state.participantEvents.filter(event => String(event.sport?.name || '').toLowerCase().includes(search));
  };

  setVisibleParticipantEventsSelected = (isSelected: boolean): void => {
    const visibleIds = this.getVisibleParticipantEvents().map(event => event.id);
    this.setState(state => ({
      selectedParticipantEventIds: isSelected
        ? Array.from(new Set([...state.selectedParticipantEventIds, ...visibleIds]))
        : state.selectedParticipantEventIds.filter(id => !visibleIds.includes(id))
    }));
  };

  openMoveWaitingConfirmation = () => {
    this.setState({
      isMoveWaitingOpen: true,
      moveWaitingError: '',
      moveWaitingCapacityWarning: '',
      participantEventSportSearch: ''
    });
    if (!isIndividualTournament(this.props.tournament)) return;
    this.setState({ isParticipantEventsLoading: true, participantEvents: [], selectedParticipantEventIds: [] });
    getTournamentEvents(
      this.props.user,
      this.props.tournament.id,
      { where: {}, skip: 0, limit: 3000, order: 'startTime ASC' },
      { summary: true }
    )
      .then((events: any[]) =>
        this.setState({
          participantEvents: events,
          selectedParticipantEventIds: events.map(event => event.id),
          isParticipantEventsLoading: false
        })
      )
      .catch(() =>
        this.setState({
          participantEvents: [],
          selectedParticipantEventIds: [],
          isParticipantEventsLoading: false,
          moveWaitingError: 'Tournament events could not be loaded. Close this window and try again.'
        })
      );
  };

  renderMoveWaitingEventSelection = (): React.ReactNode => {
    if (!isIndividualTournament(this.props.tournament)) return null;
    const visibleParticipantEvents = this.getVisibleParticipantEvents();
    const areAllVisibleEventsSelected =
      visibleParticipantEvents.length > 0 &&
      visibleParticipantEvents.every(event => this.state.selectedParticipantEventIds.includes(event.id));

    return (
      <div className="utmEligibleAddModal__events">
        <div>
          <strong>Add schools to existing events</strong>
          <span>
            {this.state.selectedParticipantEventIds.length} of {this.state.participantEvents.length} selected
          </span>
        </div>
        {this.state.participantEvents.length ? (
          <div className="utmEligibleAddModal__eventTools">
            <label>
              <Icon path={ICONS.search} size={15} />
              <input
                type="search"
                value={this.state.participantEventSportSearch}
                onChange={event => this.setState({ participantEventSportSearch: event.target.value })}
                placeholder="Search by sport"
              />
            </label>
            <div>
              <button
                type="button"
                onClick={() => this.setVisibleParticipantEventsSelected(true)}
                disabled={!visibleParticipantEvents.length || areAllVisibleEventsSelected}
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => this.setVisibleParticipantEventsSelected(false)}
                disabled={!visibleParticipantEvents.some(event => this.state.selectedParticipantEventIds.includes(event.id))}
              >
                Clear all
              </button>
            </div>
          </div>
        ) : null}
        {this.state.isParticipantEventsLoading ? <p>Loading events…</p> : null}
        {!this.state.isParticipantEventsLoading && !this.state.participantEvents.length && !this.state.moveWaitingError ? (
          <p>No existing events. The schools will be added to the tournament only.</p>
        ) : null}
        {!this.state.isParticipantEventsLoading && this.state.participantEvents.length > 0 && !visibleParticipantEvents.length ? (
          <p>No events found for this sport.</p>
        ) : null}
        {visibleParticipantEvents.map(event => (
          <label key={event.id}>
            <input
              type="checkbox"
              checked={this.state.selectedParticipantEventIds.includes(event.id)}
              onChange={() =>
                this.setState(state => ({
                  selectedParticipantEventIds: state.selectedParticipantEventIds.includes(event.id)
                    ? state.selectedParticipantEventIds.filter(id => id !== event.id)
                    : [...state.selectedParticipantEventIds, event.id]
                }))
              }
            />
            <span>
              <strong>{this.getParticipantEventName(event)}</strong>
              <small>
                {event.startTime ? moment(event.startTime).format('DD MMM YYYY, HH:mm') : 'Time not set'} ·{' '}
                {event.sport?.name || 'Sport not set'} ·{' '}
                {TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[event.gender] || event.gender || 'Gender not set'} ·{' '}
                {formatTeamAgesCompact(event.ages, 'ENGLISH') || 'Ages not set'}
              </small>
            </span>
          </label>
        ))}
      </div>
    );
  };

  openInviteConfirmation = () => this.setState({ isInviteConfirmationOpen: true, sendInvitesError: '' });

  openWaitingListConfirmation = () => this.setState({ isWaitingListConfirmationOpen: true, addToWaitingListError: '' });

  closeWaitingListConfirmation = () => {
    if (!this.state.isAddingToWaitingList) {
      this.setState({ isWaitingListConfirmationOpen: false, addToWaitingListError: '' });
    }
  };

  addSelectedSchoolsToWaitingList = async () => {
    if (this.state.isAddingToWaitingList) return;
    const schools = this.state.selectedSchools;
    const invalidSchools = schools.filter(school => school.isParticipant);
    if (invalidSchools.length) {
      this.setState({
        addToWaitingListError: `${invalidSchools
          .map(school => school.name)
          .join(', ')} cannot be added to the waiting list in their current status.`
      });
      return;
    }

    this.setState({ isAddingToWaitingList: true, addToWaitingListError: '' });
    const results = await Promise.all(
      schools.map(school =>
        addTournamentWaitingListSchool(this.props.user, this.props.tournament.id, school.id)
          .then(() => ({ school, added: true }))
          .catch(error => ({ school, added: false, error }))
      )
    );
    const failedResults = results.filter(result => !result.added);
    const failedSchools = failedResults.map(result => result.school);

    if (failedSchools.length === 0) {
      this.setState(
        {
          isWaitingListConfirmationOpen: false,
          isAddingToWaitingList: false,
          addToWaitingListError: '',
          selectedSchoolIds: [],
          selectedSchools: [],
          waitingPage: 1,
          waitingSearch: '',
          waitingSearchDraft: ''
        },
        () => {
          this.loadEligibleSchools(this.state.eligiblePage, this.state.eligibleSearch);
          this.loadWaitingList(1, '');
          this.refreshCounts();
        }
      );
    } else {
      this.setState(
        {
          isAddingToWaitingList: false,
          selectedSchoolIds: failedSchools.map(school => school.id),
          selectedSchools: failedSchools,
          addToWaitingListError: failedResults
            .map(
              (result: any) =>
                `${result.school.name}: ${getRequestErrorMessage(
                  result.error,
                  'This school could not be added to the waiting list.'
                )}`
            )
            .join(' ')
        },
        () => {
          this.loadEligibleSchools(this.state.eligiblePage, this.state.eligibleSearch);
          this.loadWaitingList(this.state.waitingPage, this.state.waitingSearch);
          this.refreshCounts();
        }
      );
    }
  };

  toggleWaitingItem = (item: TournamentWaitingListItem) => {
    this.setState(state => {
      const isSelected = state.selectedWaitingIds.includes(item.id);
      return {
        selectedWaitingIds: isSelected
          ? state.selectedWaitingIds.filter(id => id !== item.id)
          : [...state.selectedWaitingIds, item.id],
        selectedWaitingItems: isSelected
          ? state.selectedWaitingItems.filter(selected => selected.id !== item.id)
          : [...state.selectedWaitingItems, item]
      };
    });
  };

  toggleWaitingPage = () => {
    this.setState(state => {
      const pageIds = state.waitingItems.map(item => item.id);
      const isPageSelected = pageIds.length > 0 && pageIds.every(id => state.selectedWaitingIds.includes(id));
      if (isPageSelected) {
        return {
          selectedWaitingIds: state.selectedWaitingIds.filter(id => !pageIds.includes(id)),
          selectedWaitingItems: state.selectedWaitingItems.filter(item => !pageIds.includes(item.id))
        };
      }
      const newItems = state.waitingItems.filter(item => !state.selectedWaitingIds.includes(item.id));
      return {
        selectedWaitingIds: [...state.selectedWaitingIds, ...newItems.map(item => item.id)],
        selectedWaitingItems: [...state.selectedWaitingItems, ...newItems]
      };
    });
  };

  selectWaitingPage = () => {
    this.setState(state => {
      const newItems = state.waitingItems.filter(item => !state.selectedWaitingIds.includes(item.id));
      return {
        selectedWaitingIds: [...state.selectedWaitingIds, ...newItems.map(item => item.id)],
        selectedWaitingItems: [...state.selectedWaitingItems, ...newItems]
      };
    });
  };

  selectAllWaiting = async () => {
    const { user, tournament } = this.props;
    const where: any = {};
    if (this.state.waitingSearch) where.name = { like: this.state.waitingSearch, options: 'i' };
    try {
      const waitingItems = await getTournamentWaitingList(user, tournament.id, {
        where,
        limit: Math.max(this.state.counts.waiting, 1),
        skip: 0
      } as any);
      this.setState({
        selectedWaitingIds: waitingItems.map(item => item.id),
        selectedWaitingItems: waitingItems
      });
    } catch {
      this.setState({ addToWaitingListError: 'Waiting-list schools could not be selected. Please try again.' });
    }
  };

  removeSelectedFromWaitingList = async () => {
    if (this.state.isRemovingFromWaiting) return;
    const items = this.state.selectedWaitingItems;
    this.setState({ isRemovingFromWaiting: true, removeFromWaitingError: '' });
    const results = await Promise.all(
      items.map(item =>
        removeTournamentWaitingListSchool(this.props.user, this.props.tournament.id, item.id)
          .then(() => ({ item, removed: true }))
          .catch(error => ({ item, removed: false, error }))
      )
    );
    const failedResults = results.filter(result => !result.removed);
    const failedItems = failedResults.map(result => result.item);
    this.setState(
      {
        isRemovingFromWaiting: false,
        isRemoveWaitingOpen: failedItems.length > 0,
        selectedWaitingIds: failedItems.map(item => item.id),
        selectedWaitingItems: failedItems,
        removeFromWaitingError: failedResults
          .map(
            (result: any) =>
              `${result.item.name}: ${getRequestErrorMessage(
                result.error,
                'This school could not be removed from the waiting list.'
              )}`
          )
          .join(' ')
      },
      () => {
        const nextPage =
          this.state.waitingPage > 1 && this.state.waitingItems.length === items.length
            ? this.state.waitingPage - 1
            : this.state.waitingPage;
        this.loadWaitingList(nextPage, this.state.waitingSearch);
        this.loadEligibleSchools(this.state.eligiblePage, this.state.eligibleSearch);
        this.refreshCounts();
      }
    );
  };

  closeInviteConfirmation = () => {
    if (!this.state.isSendingInvites) {
      this.setState({ isInviteConfirmationOpen: false, sendInvitesError: '' });
    }
  };

  sendSelectedInvites = async () => {
    if (this.state.isSendingInvites) return;
    const schools = this.state.selectedSchools;
    const invalidSchools = schools.filter(
      school =>
        school.isParticipant ||
        (school.invite && [INVITE_STATUS.ACCEPTED, INVITE_STATUS.REJECTED].includes(school.invite.status))
    );
    if (invalidSchools.length) {
      this.setState({
        sendInvitesError: `${invalidSchools
          .map(school => school.name)
          .join(', ')} cannot receive an invitation in their current status.`
      });
      return;
    }

    this.setState({ isSendingInvites: true, sendInvitesError: '' });
    const results = await Promise.all(
      schools.map(school =>
        sendTournamentInvite(this.props.user, this.props.tournament.id, {
          invitedSchoolId: school.id,
          inviteSource: 'MANUAL_UNION_INVITE'
        })
          .then(() => ({ school, sent: true }))
          .catch(error => ({ school, sent: false, error }))
      )
    );
    const failedSchools = results.filter(result => !result.sent).map(result => result.school);

    if (failedSchools.length === 0) {
      this.setState(
        {
          isInviteConfirmationOpen: false,
          isSendingInvites: false,
          sendInvitesError: '',
          selectedSchoolIds: [],
          selectedSchools: []
        },
        () => this.loadEligibleSchools(this.state.eligiblePage, this.state.eligibleSearch)
      );
    } else {
      this.setState(
        {
          isSendingInvites: false,
          selectedSchoolIds: failedSchools.map(school => school.id),
          selectedSchools: failedSchools,
          sendInvitesError: `Invitations could not be sent to: ${failedSchools
            .map(school => school.name)
            .join(', ')}. Please try again.`
        },
        () => this.loadEligibleSchools(this.state.eligiblePage, this.state.eligibleSearch)
      );
    }
  };

  closeAddConfirmation = () => {
    if (!this.state.isAddingParticipants) {
      this.setState({ isAddConfirmationOpen: false, addParticipantsError: '', participantEvents: [], selectedParticipantEventIds: [], participantEventSportSearch: '' });
    }
  };

  createDefaultTournamentTeams = (schools: any[]) => {
    const { user, tournament } = this.props;
    const { id: tournamentId, gender, ages, sportId } = tournament;
    let teamsData: any[];

    if (isIndividualTournament(tournament)) {
      teamsData = schools.map(school => ({
        name: school.name,
        ages,
        gender,
        sportId,
        schoolId: school.id,
        isAutoEnrolledTeam: true,
        teamType: TEAM_TYPE.ADHOC,
        players: []
      }));
    } else {
      const teamAge = Array.isArray(ages) ? ages[0] : ages;
      const teamAgeConverted = AGE_GROUPS['U5-U18'][teamAge];
      const baseName =
        gender === GENDER.MALE_ONLY || gender === GENDER.FEMALE_ONLY
          ? teamAgeConverted
          : `${teamAgeConverted} ${TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[gender]}`;
      teamsData = schools.map(school => ({
        name: baseName,
        ages,
        gender,
        sportId,
        schoolId: school.id,
        isAutoEnrolledTeam: true,
        teamType: TEAM_TYPE.ADHOC,
        players: []
      }));
    }

    return addTournamentTeamsBatch(user, tournamentId, teamsData);
  };

  addSelectedParticipants = async (bypassTeamLimit = false) => {
    if (this.state.isAddingParticipants || this.state.isParticipantEventsLoading) return;
    const schools = this.state.selectedSchools;
    const invalidSchools = schools.filter(school => school.isParticipant);
    if (invalidSchools.length) {
      this.setState({
        addParticipantsError: `${invalidSchools.map(school => school.name).join(', ')} ${
          invalidSchools.length === 1 ? 'is already' : 'are already'
        } in the tournament.`
      });
      return;
    }

    const isIndividual = isIndividualTournament(this.props.tournament);
    if (!bypassTeamLimit) {
      let currentBooked: number;
      try {
        currentBooked = isIndividual
          ? this.state.counts.participants
          : (await getAllSchoolUnionTournamentTeams(this.props.user, this.props.tournament.id)).length;
      } catch (error) {
        this.setState({ addParticipantsError: 'Capacity could not be checked. Please try again.' });
        return;
      }
      const { capacity, unlimited } = getTournamentCapacitySummary(this.props.tournament, currentBooked);
      if (!unlimited) {
        this.setState({ isAddingParticipants: true, addParticipantsError: '' });
        try {
          const projected = currentBooked + schools.length;
          if (projected > capacity) {
            this.setState({
              isAddingParticipants: false,
              isParticipantLimitWarningOpen: true,
              participantLimitWarning: `The tournament capacity is ${capacity} ${isIndividual ? 'place' : 'team'}${
                capacity === 1 ? '' : 's'
              }. Adding ${schools.length} school${schools.length === 1 ? '' : 's'} will make ${projected}.`
            });
            return;
          }
        } catch (error) {
          this.setState({
            isAddingParticipants: false,
            addParticipantsError: 'Team capacity could not be checked. Please try again.'
          });
          return;
        }
      }
    }

    this.setState({ isAddingParticipants: true, addParticipantsError: '' });
    try {
      const participants: any[] = await addTournamentParticipants(this.props.user, this.props.tournament.id, {
        participants: schools.map(school => school.id),
        eventIds: isIndividual ? this.state.selectedParticipantEventIds : undefined
      });
      await this.createDefaultTournamentTeams(participants.map(participant => participant.school));
      this.setState(
        {
          isAddConfirmationOpen: false,
          isParticipantLimitWarningOpen: false,
          isAddingParticipants: false,
          addParticipantsError: '',
          selectedSchoolIds: [],
          selectedSchools: []
        },
        () => {
          this.loadEligibleSchools(this.state.eligiblePage, this.state.eligibleSearch);
          this.refreshCounts();
        }
      );
    } catch (error) {
      const responseMessage = (error as any)?.response?.data?.message || (error as any)?.response?.data?.error?.message;
      this.setState({
        isAddingParticipants: false,
        isParticipantLimitWarningOpen: false,
        addParticipantsError:
          responseMessage ||
          `${schools.map(school => school.name).join(', ')} could not be added to the tournament. Please try again.`
      });
      this.loadEligibleSchools(this.state.eligiblePage, this.state.eligibleSearch);
      this.refreshCounts();
    }
  };

  closeRemoveConfirmation = () => {
    if (!this.state.isRemovingParticipants) {
      this.setState({ isRemoveConfirmationOpen: false, removeParticipantsError: '' });
    }
  };

  removeSelectedParticipants = async () => {
    if (this.state.isRemovingParticipants) return;

    const selectedSchools = this.state.selectedSchools;
    const participants = selectedSchools.filter(school => school.isParticipant && school.participantId);
    const invalidSchools = selectedSchools.filter(school => !school.isParticipant || !school.participantId);
    if (invalidSchools.length) {
      this.setState({
        removeParticipantsError: `${invalidSchools.map(school => school.name).join(', ')} ${
          invalidSchools.length === 1 ? 'is' : 'are'
        } no longer registered as a tournament participant. Refresh the list and try again.`
      });
      return;
    }

    this.setState({ isRemovingParticipants: true, removeParticipantsError: '' });
    const results = await Promise.all(
      participants.map(school =>
        removeTournamentParticipant(this.props.user, this.props.tournament.id, school.participantId as string)
          .then(() => ({ school, removed: true }))
          .catch(error => ({ school, removed: false, error }))
      )
    );
    const failed = results.filter(result => !result.removed);

    if (failed.length === 0) {
      this.setState(
        {
          isRemoveConfirmationOpen: false,
          isRemovingParticipants: false,
          removeParticipantsError: '',
          selectedSchoolIds: [],
          selectedSchools: []
        },
        () => {
          this.loadEligibleSchools(this.state.eligiblePage, this.state.eligibleSearch);
          this.refreshCounts();
        }
      );
    } else {
      const failedSchools = failed.map(result => result.school);
      const failedNames = failedSchools.map(school => school.name).join(', ');
      this.setState(
        {
          isRemovingParticipants: false,
          selectedSchoolIds: failedSchools.map(school => school.id),
          selectedSchools: failedSchools,
          removeParticipantsError: `${failedNames} cannot be removed because ${
            failedSchools.length === 1 ? 'it is' : 'they are'
          } used in tournament events or competition groups. Remove those links first and try again.`
        },
        () => {
          this.loadEligibleSchools(this.state.eligiblePage, this.state.eligibleSearch);
          this.refreshCounts();
        }
      );
    }
  };

  toggleParticipant = (participant: any) => {
    this.setState(state => {
      const selected = state.selectedParticipantIds.includes(participant.id);
      return {
        selectedParticipantIds: selected
          ? state.selectedParticipantIds.filter(id => id !== participant.id)
          : [...state.selectedParticipantIds, participant.id],
        selectedParticipantItems: selected
          ? state.selectedParticipantItems.filter(item => item.id !== participant.id)
          : [...state.selectedParticipantItems, participant]
      };
    });
  };

  moveParticipantsToWaiting = async () => {
    if (this.state.isMovingParticipants) return;
    this.setState({ isMovingParticipants: true, moveParticipantsError: '' });
    try {
      await moveTournamentParticipantsToWaitingList(
        this.props.user,
        this.props.tournament.id,
        this.state.selectedParticipantItems.map(participant => String(participant.schoolId || participant.school?.id))
      );
      this.setState(
        {
          isMovingParticipants: false,
          isMoveParticipantsOpen: false,
          selectedParticipantIds: [],
          selectedParticipantItems: [],
          moveParticipantsError: ''
        },
        () => {
          this.loadParticipants(this.state.participantsPage, this.state.participantsSearch);
          this.loadWaitingList(1, '');
          this.loadEligibleSchools(this.state.eligiblePage, this.state.eligibleSearch);
          this.refreshCounts();
        }
      );
    } catch (error) {
      this.setState({
        isMovingParticipants: false,
        moveParticipantsError: getParticipantMoveError(error)
      });
    }
  };

  moveWaitingToParticipants = async (bypassCapacity = false) => {
    if (this.state.isMovingWaiting) return;
    const items = this.state.selectedWaitingItems;
    const isIndividual = isIndividualTournament(this.props.tournament);
    if (!bypassCapacity) {
      let currentBooked: number;
      try {
        currentBooked = isIndividual
          ? this.state.counts.participants
          : (await getAllSchoolUnionTournamentTeams(this.props.user, this.props.tournament.id)).length;
      } catch (error) {
        this.setState({ moveWaitingError: 'Capacity could not be checked. Please try again.' });
        return;
      }
      const { capacity, unlimited } = getTournamentCapacitySummary(this.props.tournament, currentBooked);
      if (!unlimited) {
        if (currentBooked + items.length > capacity) {
          this.setState({
            moveWaitingCapacityWarning: `Capacity is ${capacity} ${isIndividual ? 'places' : 'teams'}. Moving ${
              items.length
            } school${items.length === 1 ? '' : 's'} will make ${currentBooked + items.length}.`
          });
          return;
        }
      }
    }
    this.setState({ isMovingWaiting: true, moveWaitingError: '' });
    try {
      await moveTournamentWaitingListToParticipants(
        this.props.user,
        this.props.tournament.id,
        items.map(item => item.id),
        isIndividual ? this.state.selectedParticipantEventIds : undefined
      );
      this.setState(
        {
          isMovingWaiting: false,
          isMoveWaitingOpen: false,
          selectedWaitingIds: [],
          selectedWaitingItems: [],
          moveWaitingCapacityWarning: ''
        },
        () => {
          this.loadWaitingList(this.state.waitingPage, this.state.waitingSearch);
          this.loadParticipants(1, '');
          this.loadEligibleSchools(this.state.eligiblePage, this.state.eligibleSearch);
          this.refreshCounts();
        }
      );
    } catch (error) {
      this.setState({
        isMovingWaiting: false,
        moveWaitingError: getRequestErrorMessage(error, 'Schools could not be moved to participants.')
      });
    }
  };

  onAthleteSchoolsCountChanged = (athleteSchools: number) => {
    this.setState(state => ({ counts: { ...state.counts, athleteSchools } }));
  };

  refreshCounts = (force = true) => {
    const { user, tournament } = this.props;
    // The open athletes' schools list supplies its count without a duplicate request.
    if (hasUnionParticipant(tournament) && this.props.activeSection !== 'athleteSchools') {
      getTournamentNotificationAthleteSchools(user, tournament.id)
        .then(schools => this.onAthleteSchoolsCountChanged(schools.length))
        .catch(() => undefined);
    }
    getTournamentManagementSummary(user, tournament.id, force)
      .then(summary =>
        this.setState(state => ({
          counts: {
            ...state.counts,
            eligible: summary.eligible,
            participants: summary.participants,
            schoolAdmins: summary.schoolAdmins,
            waiting: summary.waiting,
            requests: summary.confirmationTotal,
            log: summary.logs
          }
        }))
      )
      .catch(() => undefined);
  };

  renderRemoveConfirmation() {
    const { isRemoveConfirmationOpen, isRemovingParticipants, removeParticipantsError, selectedSchools } = this.state;
    const names = selectedSchools.map(school => school.name);

    return (
      <SimpleModal
        isOpen={isRemoveConfirmationOpen}
        title={selectedSchools.length === 1 ? 'Remove school from tournament?' : 'Remove schools from tournament?'}
        buttonText={
          isRemovingParticipants ? 'Removing…' : selectedSchools.length === 1 ? 'Remove school' : 'Remove schools'
        }
        buttonCancelText="Cancel"
        onButtonClick={this.removeSelectedParticipants}
        onCloseClick={this.closeRemoveConfirmation}
        customClass="unionTournamentDeleteModal"
        dialogStyle={{ maxWidth: 520 }}
        isBodyBold={false}
      >
        <div className="unionTournamentDeleteModal__body">
          <div className="unionTournamentDeleteModal__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M12 8v5" />
              <path d="M12 17h.01" />
              <path d="M10.3 3.9 2.4 17.6A2 2 0 0 0 4.1 20h15.8a2 2 0 0 0 1.7-2.4L13.7 3.9a2 2 0 0 0-3.4 0Z" />
            </svg>
          </div>
          <div className="unionTournamentDeleteModal__copy">
            <strong>{names.length === 1 ? names[0] : `${names.length} selected schools`}</strong>
            <p>
              {names.length === 1
                ? 'The school will be removed from the participant list.'
                : 'The selected schools will be removed from the participant list.'}
              {' Their tournament invite will be marked as rejected.'}
            </p>
            <p>This action may be blocked if a school is already used in an event or competition group.</p>
            {removeParticipantsError ? <div className="utmEligible__removeError">{removeParticipantsError}</div> : null}
          </div>
        </div>
      </SimpleModal>
    );
  }

  renderParticipantLimitWarning() {
    return (
      <SimpleModal
        isOpen={this.state.isParticipantLimitWarningOpen}
        isBare
        onBackdropClick={() =>
          !this.state.isAddingParticipants && this.setState({ isParticipantLimitWarningOpen: false })
        }
      >
        <section className="utmTeamLimitWarning" role="alertdialog" aria-modal="true">
          <header>
            <div>
              <h2>Team limit will be exceeded</h2>
              <p>The organising union can override this limit.</p>
            </div>
            <button
              type="button"
              aria-label="Close"
              disabled={this.state.isAddingParticipants}
              onClick={() => this.setState({ isParticipantLimitWarningOpen: false })}
            >
              ×
            </button>
          </header>
          <div>
            <p>{this.state.participantLimitWarning}</p>
            <small>
              Each selected school will be added as a participant and receive its default team. This exception is
              available only to the union administrator organising the tournament.
            </small>
          </div>
          <footer>
            <button
              type="button"
              disabled={this.state.isAddingParticipants}
              onClick={() => this.setState({ isParticipantLimitWarningOpen: false })}
            >
              Go back
            </button>
            <button
              type="button"
              className="is-warning"
              disabled={this.state.isAddingParticipants}
              onClick={() => this.addSelectedParticipants(true)}
            >
              {this.state.isAddingParticipants ? 'Adding…' : 'Add anyway'}
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  renderAddConfirmation() {
    const { isAddConfirmationOpen, isAddingParticipants, addParticipantsError, selectedSchools } = this.state;
    const visibleParticipantEvents = this.getVisibleParticipantEvents();
    const areAllVisibleEventsSelected = visibleParticipantEvents.length > 0 && visibleParticipantEvents.every(event =>
      this.state.selectedParticipantEventIds.includes(event.id)
    );
    return (
      <SimpleModal isOpen={isAddConfirmationOpen} isBare onBackdropClick={this.closeAddConfirmation}>
        <section className="utmEligibleAddModal" role="dialog" aria-modal="true" aria-labelledby="add-schools-title">
          <header className="utmEligibleAddModal__header">
            <div className="utmEligibleAddModal__headingIcon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M19 8v6M16 11h6" />
              </svg>
            </div>
            <div>
              <h2 id="add-schools-title">
                {selectedSchools.length === 1 ? 'Add school to tournament' : 'Add schools to tournament'}
              </h2>
              <p>Confirm the schools joining this tournament.</p>
            </div>
            <button
              type="button"
              onClick={this.closeAddConfirmation}
              disabled={isAddingParticipants}
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <div className="utmEligibleAddModal__body">
            <div className="utmEligibleAddModal__summary">
              <span>{selectedSchools.length === 1 ? 'Selected school' : 'Selected schools'}</span>
              <b>{selectedSchools.length}</b>
            </div>
            <div className="utmEligibleAddModal__schools">
              {selectedSchools.map(school => (
                <div key={school.id}>
                  <span aria-hidden="true">
                    <img src={(school as any).pic || TOURNAMENT_SCHOOL_LOGO_FALLBACK} alt="" />
                  </span>
                  <strong>{school.name}</strong>
                </div>
              ))}
            </div>
            <div className="utmEligibleAddModal__notice">
              <Icon path={ICONS.edit} size={16} />
              <span>
                The appropriate default tournament {selectedSchools.length === 1 ? 'team' : 'teams'} will be created
                automatically.
              </span>
            </div>
            {isIndividualTournament(this.props.tournament) ? (
              <div className="utmEligibleAddModal__events">
                <div>
                  <strong>Add schools to existing events</strong>
                  <span>{this.state.selectedParticipantEventIds.length} of {this.state.participantEvents.length} selected</span>
                </div>
                {this.state.participantEvents.length ? <div className="utmEligibleAddModal__eventTools">
                  <label>
                    <Icon path={ICONS.search} size={15} />
                    <input
                      type="search"
                      value={this.state.participantEventSportSearch}
                      onChange={event => this.setState({ participantEventSportSearch: event.target.value })}
                      placeholder="Search by sport"
                    />
                  </label>
                  <label className="utmEligibleAddModal__selectAll">
                    <input type="checkbox" checked={areAllVisibleEventsSelected} onChange={event => this.setVisibleParticipantEventsSelected(event.target.checked)} disabled={!visibleParticipantEvents.length} />
                    <span aria-hidden="true" />
                    Select all{this.state.participantEventSportSearch.trim() ? ' results' : ''}
                  </label>
                </div> : null}
                {this.state.isParticipantEventsLoading ? <p>Loading events…</p> : null}
                {!this.state.isParticipantEventsLoading && this.state.participantEvents.length === 0 ? (
                  <p>No existing events. The schools will be added to the tournament only.</p>
                ) : null}
                {!this.state.isParticipantEventsLoading && this.state.participantEvents.length > 0 && visibleParticipantEvents.length === 0 ? (
                  <p>No events found for this sport.</p>
                ) : null}
                {visibleParticipantEvents.map(event => (
                  <label key={event.id}>
                    <input
                      type="checkbox"
                      checked={this.state.selectedParticipantEventIds.includes(event.id)}
                      onChange={() => this.setState(state => ({
                        selectedParticipantEventIds: state.selectedParticipantEventIds.includes(event.id)
                          ? state.selectedParticipantEventIds.filter(id => id !== event.id)
                          : [...state.selectedParticipantEventIds, event.id]
                      }))}
                    />
                    <span>
                      <strong>{this.getParticipantEventName(event)}</strong>
                      <small>
                        {event.startTime ? moment(event.startTime).format('DD MMM YYYY, HH:mm') : 'Time not set'}
                        {' · '}{event.sport?.name || 'Sport not set'}
                        {' · '}{TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[event.gender] || event.gender || 'Gender not set'}
                        {' · '}{formatTeamAgesCompact(event.ages, 'ENGLISH') || 'Ages not set'}
                      </small>
                    </span>
                  </label>
                ))}
              </div>
            ) : null}
            {addParticipantsError ? <div className="utmEligible__removeError">{addParticipantsError}</div> : null}
          </div>
          <footer className="utmEligibleAddModal__footer">
            <button type="button" onClick={this.closeAddConfirmation} disabled={isAddingParticipants}>
              Cancel
            </button>
            <button
              type="button"
              className="is-primary"
              onClick={() => this.addSelectedParticipants(false)}
              disabled={isAddingParticipants || this.state.isParticipantEventsLoading}
            >
              {isAddingParticipants
                ? 'Adding…'
                : selectedSchools.length === 1
                ? 'Add school'
                : `Add ${selectedSchools.length} schools`}
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  renderInviteConfirmation() {
    const { isInviteConfirmationOpen, isSendingInvites, sendInvitesError, selectedSchools } = this.state;
    return (
      <SimpleModal isOpen={isInviteConfirmationOpen} isBare onBackdropClick={this.closeInviteConfirmation}>
        <section
          className="utmEligibleAddModal utmEligibleAddModal--invite"
          role="dialog"
          aria-modal="true"
          aria-labelledby="send-invites-title"
        >
          <header className="utmEligibleAddModal__header">
            <div className="utmEligibleAddModal__headingIcon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M4 6h16v12H4z" />
                <path d="m4 7 8 6 8-6" />
              </svg>
            </div>
            <div>
              <h2 id="send-invites-title">
                {selectedSchools.length === 1 ? 'Send tournament invite' : 'Send tournament invites'}
              </h2>
              <p>Confirm the schools that will receive an invitation.</p>
            </div>
            <button type="button" onClick={this.closeInviteConfirmation} disabled={isSendingInvites} aria-label="Close">
              ×
            </button>
          </header>
          <div className="utmEligibleAddModal__body">
            <div className="utmEligibleAddModal__summary">
              <span>{selectedSchools.length === 1 ? 'Recipient' : 'Recipients'}</span>
              <b>{selectedSchools.length}</b>
            </div>
            <div className="utmEligibleAddModal__schools">
              {selectedSchools.map(school => (
                <div key={school.id}>
                  <span aria-hidden="true">
                    <img src={(school as any).pic || TOURNAMENT_SCHOOL_LOGO_FALLBACK} alt="" />
                  </span>
                  <strong>{school.name}</strong>
                </div>
              ))}
            </div>
            <div className="utmEligibleAddModal__notice">
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path d="M4 6h16v12H4z" />
                <path d="m4 7 8 6 8-6" />
              </svg>
              <span>Each school will receive a tournament invitation and can respond through its school account.</span>
            </div>
            {sendInvitesError ? <div className="utmEligible__removeError">{sendInvitesError}</div> : null}
          </div>
          <footer className="utmEligibleAddModal__footer">
            <button type="button" onClick={this.closeInviteConfirmation} disabled={isSendingInvites}>
              Cancel
            </button>
            <button type="button" className="is-primary" onClick={this.sendSelectedInvites} disabled={isSendingInvites}>
              {isSendingInvites
                ? 'Sending…'
                : selectedSchools.length === 1
                ? 'Send invite'
                : `Send ${selectedSchools.length} invites`}
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  renderWaitingListConfirmation() {
    const { isWaitingListConfirmationOpen, isAddingToWaitingList, addToWaitingListError, selectedSchools } = this.state;
    return (
      <SimpleModal isOpen={isWaitingListConfirmationOpen} isBare onBackdropClick={this.closeWaitingListConfirmation}>
        <section
          className="utmEligibleAddModal utmEligibleAddModal--waiting"
          role="dialog"
          aria-modal="true"
          aria-labelledby="waiting-list-title"
        >
          <header className="utmEligibleAddModal__header">
            <div className="utmEligibleAddModal__headingIcon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
            </div>
            <div>
              <h2 id="waiting-list-title">Add to waiting list</h2>
              <p>Confirm the schools joining the tournament queue.</p>
            </div>
            <button
              type="button"
              onClick={this.closeWaitingListConfirmation}
              disabled={isAddingToWaitingList}
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <div className="utmEligibleAddModal__body">
            <div className="utmEligibleAddModal__summary">
              <span>{selectedSchools.length === 1 ? 'Selected school' : 'Selected schools'}</span>
              <b>{selectedSchools.length}</b>
            </div>
            <div className="utmEligibleAddModal__schools">
              {selectedSchools.map(school => (
                <div key={school.id}>
                  <span aria-hidden="true">
                    <img src={(school as any).pic || TOURNAMENT_SCHOOL_LOGO_FALLBACK} alt="" />
                  </span>
                  <strong>{school.name}</strong>
                </div>
              ))}
            </div>
            <div className="utmEligibleAddModal__notice">
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              <span>
                {selectedSchools.length === 1 ? 'The school' : 'These schools'} will be placed on the waiting list and
                the tournament invitation status will be set to Accepted.
              </span>
            </div>
            {addToWaitingListError ? <div className="utmEligible__removeError">{addToWaitingListError}</div> : null}
          </div>
          <footer className="utmEligibleAddModal__footer">
            <button type="button" onClick={this.closeWaitingListConfirmation} disabled={isAddingToWaitingList}>
              Cancel
            </button>
            <button
              type="button"
              className="is-primary"
              onClick={this.addSelectedSchoolsToWaitingList}
              disabled={isAddingToWaitingList}
            >
              {isAddingToWaitingList
                ? 'Adding…'
                : selectedSchools.length === 1
                ? 'Add school'
                : `Add ${selectedSchools.length} schools`}
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  renderEligibleCriteria() {
    const { eligibleSearch, filters, areas } = this.state;
    const genderLabels = { MALE: 'Boys', FEMALE: 'Girls', MIXED: 'Mixed' };
    const statusLabel = status =>
      status === 'NOT_READY'
        ? 'Awaiting response'
        : status === 'NOT_SENT'
        ? 'Not sent'
        : status.charAt(0) + status.slice(1).toLowerCase();
    const criteria: Array<{ key: string; label: string; onClear: () => void }> = [];

    if (eligibleSearch) {
      criteria.push({
        key: 'search',
        label: `School: ${eligibleSearch}`,
        onClear: () =>
          this.setState({ eligibleSearch: '', eligibleSearchDraft: '' }, () => this.loadEligibleSchools(1, ''))
      });
    }
    if (filters.ages.length) {
      criteria.push({
        key: 'ages',
        label: `Available ages: ${filters.ages.map(age => AGE_GROUPS.ENGLISH[age]).join(', ')}`,
        onClear: () => this.clearEligibleFilter('ages')
      });
    }
    if (filters.genders.length) {
      criteria.push({
        key: 'genders',
        label: `Available gender: ${filters.genders.map(gender => genderLabels[gender] || gender).join(', ')}`,
        onClear: () => this.clearEligibleFilter('genders')
      });
    }
    if (filters.participant !== '') {
      criteria.push({
        key: 'participant',
        label: `Participant: ${filters.participant === 'true' ? 'Yes' : 'No'}`,
        onClear: () => this.clearEligibleFilter('participant')
      });
    }
    if (filters.statuses.length) {
      criteria.push({
        key: 'statuses',
        label: `Invite status: ${filters.statuses.map(statusLabel).join(', ')}`,
        onClear: () => this.clearEligibleFilter('statuses')
      });
    }
    if (filters.dateFrom || filters.dateTo) {
      criteria.push({
        key: 'dates',
        label: `Invite updated: ${filters.dateFrom ? moment(filters.dateFrom).format('DD-MM-YYYY') : 'Any'} – ${
          filters.dateTo ? moment(filters.dateTo).format('DD-MM-YYYY') : 'Any'
        }`,
        onClear: () => {
          this.setState(
            state => ({ filters: { ...state.filters, dateFrom: '', dateTo: '' } }),
            () => this.loadEligibleSchools(1, this.state.eligibleSearch)
          );
        }
      });
    }
    if (filters.areas.length) {
      criteria.push({
        key: 'areas',
        label: `Area: ${filters.areas
          .map(areaId => (areas.find(area => area.id === areaId) || {}).name || areaId)
          .join(', ')}`,
        onClear: () => this.clearEligibleFilter('areas')
      });
    }

    return criteria.length ? (
      <div className="utmEligible__criteria">
        {criteria.map(criterion => (
          <span key={criterion.key}>
            {criterion.label}
            <button type="button" aria-label={`Clear ${criterion.label}`} onClick={criterion.onClear}>
              ×
            </button>
          </span>
        ))}
      </div>
    ) : null;
  }

  renderEligibleFilters() {
    if (!this.state.isFiltersOpen) return null;
    const filters = this.state.eligibleFilterDraft;
    const statusOptions = [
      INVITE_STATUS.ACCEPTED,
      INVITE_STATUS.REJECTED,
      INVITE_STATUS.PREBOOKED,
      INVITE_STATUS.NOT_READY,
      INVITE_STATUS.OUTDATED,
      INVITE_STATUS.CANCELED,
      'NOT_SENT'
    ];
    return (
      <div className="utmEligiblePanelBackdrop" onMouseDown={() => this.setState({ isFiltersOpen: false })}>
        <form
          className="utmEligiblePanel"
          onSubmit={this.applyEligibleFilters}
          onKeyDown={this.onEligibleFiltersKeyDown}
          onMouseDown={event => event.stopPropagation()}
        >
          <header>
            <h2>Filters</h2>
            <button type="button" onClick={() => this.setState({ isFiltersOpen: false })}>
              ×
            </button>
          </header>
          <div className="utmEligiblePanel__body">
            <section>
              <h3>Available ages</h3>
              <div className="utmEligiblePanel__chips">
                {AGE_GROUPS.ENGLISH.slice(0, 14).map((label, index) => (
                  <button
                    key={label}
                    type="button"
                    className={filters.ages.includes(index) ? 'is-selected' : ''}
                    onClick={() => this.toggleFilterValue('ages', index)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </section>
            <section>
              <h3>Available gender</h3>
              <div className="utmEligiblePanel__chips">
                {[
                  ['MALE', 'Boys'],
                  ['FEMALE', 'Girls'],
                  ['MIXED', 'Mixed']
                ].map(option => (
                  <button
                    key={option[0]}
                    type="button"
                    className={filters.genders.includes(option[0]) ? 'is-selected' : ''}
                    onClick={() => this.toggleFilterValue('genders', option[0])}
                  >
                    {option[1]}
                  </button>
                ))}
              </div>
            </section>
            <section>
              <h3>Participant</h3>
              <div className="utmEligiblePanel__chips">
                {[
                  ['true', 'Yes'],
                  ['false', 'No']
                ].map(option => (
                  <button
                    key={option[0]}
                    type="button"
                    className={filters.participant === option[0] ? 'is-selected' : ''}
                    onClick={() =>
                      this.setState({
                        eligibleFilterDraft: {
                          ...filters,
                          participant: filters.participant === option[0] ? '' : option[0]
                        }
                      })
                    }
                  >
                    {option[1]}
                  </button>
                ))}
              </div>
            </section>
            <section>
              <h3>Invite status</h3>
              <div className="utmEligiblePanel__chips">
                {statusOptions.map(status => (
                  <button
                    key={status}
                    type="button"
                    className={filters.statuses.includes(status) ? 'is-selected' : ''}
                    onClick={() =>
                      this.setState({
                        eligibleFilterDraft: {
                          ...filters,
                          statuses: filters.statuses.includes(status) ? [] : [status]
                        }
                      })
                    }
                  >
                    {status === 'NOT_READY'
                      ? 'Awaiting response'
                      : status === 'NOT_SENT'
                      ? 'Not sent'
                      : status.charAt(0) + status.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </section>
            <section>
              <h3>Invite updated</h3>
              <div className="utmEligiblePanel__dateRange">
                <SitDatePicker
                  mode="range"
                  value={{
                    start: filters.dateFrom ? moment(filters.dateFrom).toDate() : null,
                    end: filters.dateTo ? moment(filters.dateTo).toDate() : null
                  }}
                  onChange={value => {
                    const range = (value || { start: null, end: null }) as DateRange;
                    this.setState({
                      eligibleFilterDraft: {
                        ...filters,
                        dateFrom: range.start ? moment(range.start).format('YYYY-MM-DD') : '',
                        dateTo: range.end ? moment(range.end).format('YYYY-MM-DD') : ''
                      }
                    });
                  }}
                  format="DD-MM-YYYY"
                  placeholder="Select date range"
                  numberOfMonths={2}
                  align="left"
                  clearable={true}
                  syncRangeOnSelect={true}
                />
              </div>
            </section>
            <section>
              <h3>Area</h3>
              <div className="utmEligiblePanel__areas">
                {this.state.areas.map(area => (
                  <label key={area.id}>
                    <input
                      type="checkbox"
                      checked={filters.areas.includes(area.id)}
                      onChange={() => this.toggleFilterValue('areas', area.id)}
                    />
                    {area.name}
                  </label>
                ))}
              </div>
            </section>
          </div>
          <footer>
            <button
              type="button"
              onClick={() =>
                this.setState({
                  eligibleFilterDraft: {
                    ages: [],
                    genders: [],
                    participant: '',
                    statuses: [],
                    dateFrom: '',
                    dateTo: '',
                    areas: []
                  }
                })
              }
            >
              Clear all
            </button>
            <button type="button" onClick={() => this.setState({ isFiltersOpen: false })}>
              Cancel
            </button>
            <button type="submit" className="is-primary">
              Apply
            </button>
          </footer>
        </form>
      </div>
    );
  }

  renderColumnsModal() {
    if (!this.state.isColumnsOpen) return null;
    const options = [
      ['school', 'School'],
      ['ages', 'Available ages'],
      ['gender', 'Available gender'],
      ['participant', 'Participant'],
      ['status', 'Invite status'],
      ['date', 'Invite updated'],
      ['area', 'Area']
    ];
    return (
      <div className="utmColumnsBackdrop" onMouseDown={() => this.setState({ isColumnsOpen: false })}>
        <div className="utmColumnsModal" onMouseDown={event => event.stopPropagation()}>
          <header>
            <h2>Table columns</h2>
            <button type="button" className="utmColumnsReset" onClick={this.resetEligibleColumns}>
              Reset
            </button>
            <button type="button" onClick={() => this.setState({ isColumnsOpen: false })}>
              ×
            </button>
          </header>
          <div>
            {options.map(option => (
              <label key={option[0]}>
                <input
                  type="checkbox"
                  checked={this.state.visibleColumns.includes(option[0])}
                  disabled={option[0] === 'school'}
                  onChange={() => this.toggleEligibleColumn(option[0])}
                />
                {option[1]}
              </label>
            ))}
          </div>
          <footer>
            <button type="button" onClick={() => this.setState({ isColumnsOpen: false })}>
              Done
            </button>
          </footer>
        </div>
      </div>
    );
  }

  renderRules() {
    const { tournament } = this.props;
    const { booked, capacity, unlimited } = getTournamentCapacitySummary(tournament, this.state.counts.participants);
    const left = unlimited ? 'Unlimited' : String(Math.max(0, capacity - booked));
    const percent = unlimited || capacity === 0 ? 0 : Math.min(100, (booked / capacity) * 100);
    const rules = [
      {
        label: 'Autoenrollment',
        hint: 'Schools can enter themselves without organiser approval.',
        value: tournament.isAutoEnrollmentTeams
      },
      {
        label: 'Enrollment onto the waiting list only',
        hint: 'Every new entry joins the queue instead of taking a place.',
        value: tournament.isAutoEnrollmentToWaitingList
      },
      {
        label: 'Move to waiting list once full',
        hint: 'When the last place goes, further entries queue automatically.',
        value: tournament.isEnableWaitingListWhenFull
      },
      {
        label: 'Schools in read-only mode',
        hint: 'Schools can see the tournament but cannot change their entry.',
        value: tournament.isReadOnly
      }
    ];

    return (
      <div className="utmEntries__content">
        <article className="utmEntries__panel">
          <header>
            <div>
              <h2>Rules & capacity</h2>
              <p>
                How schools get in and how many places exist. These rules drive the waiting list and the enrollment log.
              </p>
            </div>
            <button type="button" onClick={this.openRulesEditor}>
              <Icon path={ICONS.edit} /> Edit rules
            </button>
          </header>
          <div className="utmEntries__capacity">
            <div>
              <span>Total places</span>
              <strong>{unlimited ? 'Unlimited' : capacity}</strong>
            </div>
            <div>
              <span>Booked</span>
              <strong>{unlimited ? booked : `${booked} of ${capacity}`}</strong>
            </div>
            <div>
              <span>Places left</span>
              <strong>{left}</strong>
            </div>
            <div>
              <span>{isIndividualTournament(tournament) ? 'Entries per school' : 'Max teams per school'}</span>
              <strong>
                {isIndividualTournament(tournament) ? '1' : tournament.maximumNumberTeamsOfSchool || 'Unlimited'}
              </strong>
            </div>
          </div>
          <div className="utmEntries__capacityBar">
            <i style={{ width: `${percent}%` }} />
          </div>
        </article>

        <article className="utmEntries__panel">
          <header>
            <div>
              <h2>Enrollment rules</h2>
            </div>
          </header>
          <div className="utmEntries__rules">
            {rules.map(rule => (
              <div key={rule.label}>
                <span>
                  <strong>{rule.label}</strong>
                  <small>{rule.hint}</small>
                </span>
                <b className={rule.value ? 'is-yes' : 'is-no'}>{rule.value ? 'Yes' : 'No'}</b>
              </div>
            ))}
          </div>
        </article>
        {this.state.rulesDraft ? (
          <SimpleModal isOpen={this.state.isRulesEditorOpen} isBare onBackdropClick={this.requestCloseRulesEditor}>
            <section className="utmRulesEditor" role="dialog" aria-modal="true" aria-labelledby="rules-editor-title">
              <header>
                <div>
                  <h2 id="rules-editor-title">Edit rules & capacity</h2>
                  <p>
                    {isIndividualTournament(tournament)
                      ? 'Individual tournament entry settings.'
                      : 'Team tournament entry settings.'}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close"
                  disabled={this.state.isRulesSaving}
                  onClick={this.requestCloseRulesEditor}
                >
                  ×
                </button>
              </header>
              <div className="utmRulesEditor__body">
                {[
                  [
                    'isReadOnly',
                    'Schools in read-only mode',
                    'Schools can view the tournament but cannot change entries.'
                  ],
                  ['isAutoEnrollmentTeams', 'Auto-enrolment', 'Schools can enter without organiser approval.'],
                  [
                    'isEnableWaitingListWhenFull',
                    'Queue when full',
                    'New entries join the waiting list after capacity is reached.'
                  ],
                  ['isAutoEnrollmentToWaitingList', 'Waiting list only', 'Every new entry joins the queue.']
                ].map(([field, label, hint]: [RulesBooleanField, string, string]) => {
                  const disabled =
                    field !== 'isReadOnly' &&
                    field !== 'isAutoEnrollmentTeams' &&
                    !this.state.rulesDraft!.isAutoEnrollmentTeams;
                  return (
                    <div className={`utmRulesEditor__switch ${disabled ? 'is-disabled' : ''}`} key={field}>
                      <span>
                        <strong>{label}</strong>
                        <small>{hint}</small>
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={Boolean(this.state.rulesDraft![field])}
                        disabled={disabled}
                        className={this.state.rulesDraft![field] ? 'is-on' : ''}
                        onClick={() => {
                          const value = !this.state.rulesDraft![field];
                          this.updateRulesDraft(
                            field === 'isAutoEnrollmentTeams' && !value
                              ? {
                                  isAutoEnrollmentTeams: false,
                                  isEnableWaitingListWhenFull: false,
                                  isAutoEnrollmentToWaitingList: false
                                }
                              : ({ [field]: value } as any)
                          );
                        }}
                      >
                        <i />
                      </button>
                    </div>
                  );
                })}
                <div className="utmRulesEditor__capacityFields">
                  <h3>Capacity</h3>
                  {isIndividualTournament(tournament) ? (
                    <label>
                      <span>
                        Maximum places <small>Use 0 for unlimited. Each participating school uses one place.</small>
                      </span>
                      <input
                        type="number"
                        min="0"
                        value={this.state.rulesDraft.individualCapacity}
                        onChange={event =>
                          this.updateRulesDraft({ individualCapacity: Math.max(0, Number(event.target.value)) })
                        }
                      />
                    </label>
                  ) : (
                    <>
                      {this.state.rulesDraft.settings.map((setting, index) => (
                        <label key={`${setting.age}-${setting.gender}`}>
                          <span>
                            {AGE_GROUPS.ENGLISH[setting.age] || `Age ${setting.age}`} ·{' '}
                            {TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING[setting.gender] || setting.gender}
                            <small>Maximum total teams; 0 is unlimited.</small>
                          </span>
                          <input
                            type="number"
                            min="0"
                            value={setting.maximumNumberTeams}
                            onChange={event => {
                              const settings = [...this.state.rulesDraft!.settings];
                              settings[index] = {
                                ...settings[index],
                                maximumNumberTeams: Math.max(0, Number(event.target.value))
                              };
                              this.updateRulesDraft({ settings });
                            }}
                          />
                        </label>
                      ))}
                      <label>
                        <span>
                          Max teams per school <small>0 for unlimited, otherwise 1–5.</small>
                        </span>
                        <input
                          type="number"
                          min="0"
                          max="5"
                          value={this.state.rulesDraft.maximumNumberTeamsOfSchool}
                          onChange={event =>
                            this.updateRulesDraft({ maximumNumberTeamsOfSchool: Number(event.target.value) })
                          }
                        />
                      </label>
                    </>
                  )}
                </div>
                {this.state.rulesError ? <div className="utmEligible__removeError">{this.state.rulesError}</div> : null}
              </div>
              <footer>
                <button type="button" disabled={this.state.isRulesSaving} onClick={this.requestCloseRulesEditor}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="is-primary"
                  disabled={this.state.isRulesSaving}
                  onClick={this.saveRules}
                >
                  {this.state.isRulesSaving ? 'Saving…' : 'Save changes'}
                </button>
              </footer>
            </section>
          </SimpleModal>
        ) : null}
        <UnsavedChangesModal
          isOpen={this.state.isDiscardRulesOpen}
          message="Your changes to tournament rules and capacity have not been saved. If you leave now, they will be lost."
          onKeepEditing={() => this.setState({ isDiscardRulesOpen: false })}
          onDiscard={this.discardRulesChanges}
        />
      </div>
    );
  }

  renderSectionMessage() {
    const section = SECTIONS.find(item => item.key === this.props.activeSection);
    return (
      <div className="utmEntries__content">
        <article className="utmEntries__panel utmEntries__sectionIntro">
          <header>
            <div>
              <h2>{section ? section.label : 'Entries'}</h2>
              <p>This section is ready for its new management table.</p>
            </div>
          </header>
        </article>
      </div>
    );
  }

  getEnrollmentLogSchool = (log: any): string =>
    log.affectedSchoolData?.name ||
    log.inviteData?.invitedSchoolName ||
    log.teamSchoolData?.name ||
    log.schoolData?.name ||
    '—';

  getEnrollmentLogUser = (log: any): string => {
    const name = [log.userData?.firstName, log.userData?.lastName].filter(Boolean).join(' ');
    if (name) return name;
    return String(log.action || '').includes('AUTO') ? 'Auto-enrollment' : 'System';
  };

  getEnrollmentLogTeam = (log: any): string => log.affectedTeamData?.name || log.actionDetails?.teamName || '—';

  getEnrollmentLogTone = (action: string): string => {
    if (String(action).includes('REMOVED') || action === TOURNAMENT_LOG_ACTION_TYPES.INVITE_REJECTED) return 'danger';
    if (String(action).includes('WAITING') || action === TOURNAMENT_LOG_ACTION_TYPES.INVITE_CANCELLED) return 'warning';
    if (String(action).includes('AUTO')) return 'info';
    return 'success';
  };

  exportEnrollmentLogs = async () => {
    const { enrollmentLogSearch, enrollmentLogAction, enrollmentLogDateRange } = this.state;
    const where = this.getEnrollmentLogWhere(enrollmentLogSearch, enrollmentLogAction, enrollmentLogDateRange);
    const logs = await getAllTournamentEnrollmentLogs(this.props.user, this.props.tournament.id, where).catch(() => []);
    const escape = (value: any) => `"${String(value == null ? '' : value).replace(/"/g, '""')}"`;
    const rows = [
      ['When', 'Action', 'School', 'Affected team', 'By', 'Comment'],
      ...logs.map(log => [
        moment(log.createdAt).format('DD-MM-YYYY HH:mm'),
        TOURNAMENT_LOG_ACTION_TYPES_SERVER_TO_CLIENT_MAPPING[log.action] || log.action,
        this.getEnrollmentLogSchool(log),
        this.getEnrollmentLogTeam(log) === '—' ? '' : this.getEnrollmentLogTeam(log),
        this.getEnrollmentLogUser(log),
        log.comment || ''
      ])
    ];
    const blob = new Blob([rows.map(row => row.map(escape).join(',')).join('\r\n')], {
      type: 'text/csv;charset=utf-8'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.props.tournament.name || 'tournament'}-enrolment-log.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  saveEnrollmentLogComment = async () => {
    const {
      selectedEnrollmentLogId,
      enrollmentLogComment,
      enrollmentLogSearch,
      enrollmentLogAction,
      enrollmentLogDateRange
    } = this.state;
    if (!selectedEnrollmentLogId || this.state.isEnrollmentLogCommentSaving) return;
    this.setState({ isEnrollmentLogCommentSaving: true });
    try {
      await createCommentEnrollmentLog(this.props.user, this.props.tournament.id, selectedEnrollmentLogId, {
        comment: enrollmentLogComment.trim()
      });
      this.setState({
        isEnrollmentLogCommentOpen: false,
        isEnrollmentLogCommentSaving: false,
        selectedEnrollmentLogId: '',
        openedEnrollmentLog: undefined
      });
      this.loadEnrollmentLogs(
        this.state.enrollmentLogPage,
        enrollmentLogSearch,
        enrollmentLogAction,
        enrollmentLogDateRange
      );
    } catch (error) {
      this.setState({ isEnrollmentLogCommentSaving: false });
    }
  };

  requestCloseEnrollmentLogDrawer = () => {
    const { openedEnrollmentLog, enrollmentLogComment, isEnrollmentLogCommentSaving } = this.state;
    if (!openedEnrollmentLog || isEnrollmentLogCommentSaving) return;
    if (enrollmentLogComment !== (openedEnrollmentLog.comment || '')) {
      this.setState({ isDiscardEnrollmentLogCommentOpen: true });
      return;
    }
    this.setState({ openedEnrollmentLog: undefined });
  };

  discardEnrollmentLogComment = () => {
    this.setState({
      openedEnrollmentLog: undefined,
      enrollmentLogComment: '',
      isDiscardEnrollmentLogCommentOpen: false
    });
  };

  renderEnrollmentLog() {
    const {
      enrollmentLogs,
      enrollmentLogPage,
      enrollmentLogSearch,
      enrollmentLogSearchDraft,
      enrollmentLogAction,
      enrollmentLogDateRange,
      isEnrollmentLogLoading
    } = this.state;
    const total = this.state.counts.log;
    const visible = this.state.enrollmentLogVisibleColumns;
    const columnCount = 2 + visible.length;
    const actions = Object.keys(TOURNAMENT_LOG_ACTION_TYPES).map(key => ({
      value: TOURNAMENT_LOG_ACTION_TYPES[key],
      label: TOURNAMENT_LOG_ACTION_TYPES_SERVER_TO_CLIENT_MAPPING[key]
    }));

    return (
      <div className="utmEntries__content">
        <article className="utmEntries__panel utmEnrollmentLog">
          <header>
            <div>
              <h2>
                Enrolment log <b>{total}</b>
              </h2>
            </div>
            <div className="utmEnrollmentLog__headerTools">
              <div className="gridModernSearch utmEnrollmentLog__search">
                <form onSubmit={this.onEnrollmentLogSearch}>
                  <input
                    value={enrollmentLogSearchDraft}
                    onChange={event => this.setState({ enrollmentLogSearchDraft: event.target.value })}
                    placeholder="Search by school name"
                  />
                </form>
              </div>
              <div className="gridModernFiltersAnchor">
                <button
                  type="button"
                  className={`gridModernFilterButton ${
                    enrollmentLogAction.length || enrollmentLogDateRange.start || enrollmentLogDateRange.end
                      ? 'is-active'
                      : ''
                  }`}
                  onClick={() =>
                    this.setState(state => ({
                      isEnrollmentLogFiltersOpen: true,
                      enrollmentLogActionDraft: [...state.enrollmentLogAction],
                      enrollmentLogDateRangeDraft: { ...state.enrollmentLogDateRange }
                    }))
                  }
                >
                  <span aria-hidden="true" /> Filters
                </button>
              </div>
              <div className="gridModernColumns">
                <button type="button" onClick={() => this.setState({ isEnrollmentLogColumnsOpen: true })}>
                  <span aria-hidden="true" /> Columns <b>{visible.length}</b>
                </button>
              </div>
            </div>
          </header>

          {this.state.selectedEnrollmentLogId ? (
            <div className="utmEnrollmentLog__selection">
              <strong>Selected 1</strong>
              <button
                type="button"
                onClick={() => {
                  const selected = enrollmentLogs.find(log => log.id === this.state.selectedEnrollmentLogId);
                  this.setState({ isEnrollmentLogCommentOpen: true, enrollmentLogComment: selected?.comment || '' });
                }}
              >
                Add comment
              </button>
              <button
                className="is-clearSelection"
                type="button"
                onClick={() => this.setState({ selectedEnrollmentLogId: '' })}
              >
                <span aria-hidden="true">×</span> Clear selection
              </button>
            </div>
          ) : null}

          {(enrollmentLogSearch ||
            enrollmentLogAction.length ||
            enrollmentLogDateRange.start ||
            enrollmentLogDateRange.end) && (
            <div className="utmEnrollmentLog__chips">
              {enrollmentLogSearch ? (
                <span>
                  Search: {enrollmentLogSearch}
                  <button
                    type="button"
                    aria-label="Remove school search filter"
                    onClick={() => {
                      this.setState({ enrollmentLogSearchDraft: '' });
                      this.loadEnrollmentLogs(1, '', enrollmentLogAction, enrollmentLogDateRange);
                    }}
                  >
                    ×
                  </button>
                </span>
              ) : null}
              {enrollmentLogAction.map(action => (
                <span key={action}>
                  {TOURNAMENT_LOG_ACTION_TYPES_SERVER_TO_CLIENT_MAPPING[action]}
                  <button
                    type="button"
                    aria-label={`Remove ${TOURNAMENT_LOG_ACTION_TYPES_SERVER_TO_CLIENT_MAPPING[action]} filter`}
                    onClick={() => {
                      const nextActions = enrollmentLogAction.filter(value => value !== action);
                      this.loadEnrollmentLogs(1, enrollmentLogSearch, nextActions, enrollmentLogDateRange);
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
              {enrollmentLogDateRange.start || enrollmentLogDateRange.end ? (
                <span>
                  {[enrollmentLogDateRange.start, enrollmentLogDateRange.end]
                    .filter(Boolean)
                    .map(date => moment(date).format('DD-MM-YYYY'))
                    .join(' – ')}
                  <button
                    type="button"
                    aria-label="Remove date range filter"
                    onClick={() =>
                      this.loadEnrollmentLogs(1, enrollmentLogSearch, enrollmentLogAction, {
                        start: null,
                        end: null
                      })
                    }
                  >
                    ×
                  </button>
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  this.setState({ enrollmentLogSearchDraft: '' });
                  this.loadEnrollmentLogs(1, '', [], { start: null, end: null });
                }}
              >
                Clear all
              </button>
            </div>
          )}

          <div className="utmEnrollmentLog__tableWrap">
            <table>
              <thead>
                <tr>
                  <th className="utmEnrollmentLog__selectColumn" />
                  <th>When</th>
                  {visible.includes('action') ? <th>Action</th> : null}
                  {visible.includes('school') ? <th>School</th> : null}
                  {visible.includes('team') ? <th className="utmEnrollmentLog__teamColumn">Affected team</th> : null}
                  {visible.includes('by') ? <th>By</th> : null}
                  {visible.includes('comment') ? <th>Comment</th> : null}
                </tr>
              </thead>
              <tbody>
                {isEnrollmentLogLoading ? (
                  <tr>
                    <td colSpan={columnCount} className="utmEligible__empty">
                      Loading enrolment log…
                    </td>
                  </tr>
                ) : null}
                {!isEnrollmentLogLoading &&
                  enrollmentLogs.map(log => (
                    <tr
                      key={log.id}
                      className="utmEnrollmentLog__row"
                      onClick={() =>
                        this.setState({
                          openedEnrollmentLog: log,
                          selectedEnrollmentLogId: log.id,
                          enrollmentLogComment: log.comment || ''
                        })
                      }
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={this.state.selectedEnrollmentLogId === log.id}
                          onClick={event => event.stopPropagation()}
                          onChange={() =>
                            this.setState({
                              selectedEnrollmentLogId: this.state.selectedEnrollmentLogId === log.id ? '' : log.id
                            })
                          }
                        />
                      </td>
                      <td>
                        <strong>{moment(log.createdAt).format('DD-MM-YYYY')}</strong>
                        <small>{moment(log.createdAt).format('HH:mm')}</small>
                      </td>
                      {visible.includes('action') ? (
                        <td>
                          <span className={`utmEnrollmentLog__action is-${this.getEnrollmentLogTone(log.action)}`}>
                            {TOURNAMENT_LOG_ACTION_TYPES_SERVER_TO_CLIENT_MAPPING[log.action] || log.action || '—'}
                          </span>
                        </td>
                      ) : null}
                      {visible.includes('school') ? <td>{this.getEnrollmentLogSchool(log)}</td> : null}
                      {visible.includes('team') ? (
                        <td className="utmEnrollmentLog__teamCell" title={this.getEnrollmentLogTeam(log)}>
                          {this.getEnrollmentLogTeam(log)}
                        </td>
                      ) : null}
                      {visible.includes('by') ? (
                        <td>
                          <strong>{this.getEnrollmentLogUser(log)}</strong>
                          {log.userData?.email ? <small>{log.userData.email}</small> : null}
                        </td>
                      ) : null}
                      {visible.includes('comment') ? <td>{log.comment || '—'}</td> : null}
                    </tr>
                  ))}
                {!isEnrollmentLogLoading && !enrollmentLogs.length ? (
                  <tr>
                    <td colSpan={columnCount} className="utmEligible__empty">
                      <TableEmptyState
                        filtered={Boolean(enrollmentLogSearch || enrollmentLogAction.length || enrollmentLogDateRange.start || enrollmentLogDateRange.end)}
                        emptyTitle="No enrolment activity yet"
                        emptyText="Participant, waiting-list and invitation changes will be recorded here."
                        filteredTitle="No log entries match your filters"
                        filteredText="Try a different search, action type or date range."
                        icon="M5 3h14v18H5zM8 7h8M8 11h8M8 15h5"
                      />
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <Pagination2
            modern
            currentPage={enrollmentLogPage}
            total={total}
            limit={20}
            setCurrentPageParams={page =>
              this.loadEnrollmentLogs(page, enrollmentLogSearch, enrollmentLogAction, enrollmentLogDateRange)
            }
          />
        </article>

        {this.state.isEnrollmentLogFiltersOpen ? (
          <div
            className="utmEligiblePanelBackdrop"
            onMouseDown={() => this.setState({ isEnrollmentLogFiltersOpen: false })}
          >
            <form
              className="utmEligiblePanel utmEnrollmentLogFilters"
              onMouseDown={event => event.stopPropagation()}
              onSubmit={event => {
                event.preventDefault();
                this.setState({ isEnrollmentLogFiltersOpen: false });
                this.loadEnrollmentLogs(
                  1,
                  enrollmentLogSearch,
                  this.state.enrollmentLogActionDraft,
                  this.state.enrollmentLogDateRangeDraft
                );
              }}
            >
              <header>
                <h2>Filters</h2>
                <button type="button" onClick={() => this.setState({ isEnrollmentLogFiltersOpen: false })}>
                  ×
                </button>
              </header>
              <div className="utmEligiblePanel__body">
                <section>
                  <h3>Action</h3>
                  <div className="utmEligiblePanel__chips">
                    {actions.map(action => (
                      <button
                        type="button"
                        className={this.state.enrollmentLogActionDraft.includes(action.value) ? 'is-selected' : ''}
                        key={action.value}
                        onClick={() =>
                          this.setState(state => ({
                            enrollmentLogActionDraft: state.enrollmentLogActionDraft.includes(action.value)
                              ? state.enrollmentLogActionDraft.filter(value => value !== action.value)
                              : [...state.enrollmentLogActionDraft, action.value]
                          }))
                        }
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                </section>
                <section>
                  <h3>Date range</h3>
                  <div className="utmEligiblePanel__dateRange">
                    <SitDatePicker
                      mode="range"
                      value={this.state.enrollmentLogDateRangeDraft}
                      onChange={value =>
                        this.setState({
                          enrollmentLogDateRangeDraft: (value || { start: null, end: null }) as DateRange
                        })
                      }
                      format="DD-MM-YYYY"
                      placeholder="Select date range"
                      numberOfMonths={2}
                      clearable
                      syncRangeOnSelect
                    />
                  </div>
                </section>
              </div>
              <footer>
                <button
                  type="button"
                  onClick={() => {
                    this.setState({
                      enrollmentLogActionDraft: [],
                      enrollmentLogDateRangeDraft: { start: null, end: null }
                    });
                  }}
                >
                  Clear all
                </button>
                <button type="button" onClick={() => this.setState({ isEnrollmentLogFiltersOpen: false })}>
                  Cancel
                </button>
                <button className="is-primary" type="submit">
                  Apply
                </button>
              </footer>
            </form>
          </div>
        ) : null}

        {this.state.isEnrollmentLogColumnsOpen ? (
          <div className="utmColumnsBackdrop" onMouseDown={() => this.setState({ isEnrollmentLogColumnsOpen: false })}>
            <div className="utmColumnsModal" onMouseDown={event => event.stopPropagation()}>
              <header>
                <h2>Table columns</h2>
                <button type="button" className="utmColumnsReset" onClick={this.resetEnrollmentLogColumns}>
                  Reset
                </button>
                <button type="button" onClick={() => this.setState({ isEnrollmentLogColumnsOpen: false })}>
                  ×
                </button>
              </header>
              <div>
                {[
                  ['action', 'Action'],
                  ['school', 'School'],
                  ['team', 'Affected team'],
                  ['by', 'By'],
                  ['comment', 'Comment']
                ].map(([key, label]) => (
                  <label key={key}>
                    <input
                      type="checkbox"
                      checked={visible.includes(key)}
                      onChange={() => this.toggleEnrollmentLogColumn(key)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <footer>
                <button type="button" onClick={() => this.setState({ isEnrollmentLogColumnsOpen: false })}>
                  Done
                </button>
              </footer>
            </div>
          </div>
        ) : null}

        {this.state.openedEnrollmentLog && !this.state.isDiscardEnrollmentLogCommentOpen ? (
          <div
            className="utmConfirmationDrawer utmEnrollmentLogDrawer"
            onMouseDown={event => event.target === event.currentTarget && this.requestCloseEnrollmentLogDrawer()}
          >
            <aside>
              <header>
                <div>
                  <h3>{this.getEnrollmentLogSchool(this.state.openedEnrollmentLog)}</h3>
                  <p>Enrolment log details</p>
                </div>
                <button type="button" onClick={this.requestCloseEnrollmentLogDrawer}>
                  ×
                </button>
              </header>
              <div className="utmConfirmationDrawer__body">
                <span
                  className={`utmEnrollmentLog__action is-${this.getEnrollmentLogTone(
                    this.state.openedEnrollmentLog.action
                  )}`}
                >
                  {TOURNAMENT_LOG_ACTION_TYPES_SERVER_TO_CLIENT_MAPPING[this.state.openedEnrollmentLog.action] ||
                    this.state.openedEnrollmentLog.action ||
                    '—'}
                </span>
                <dl>
                  <div>
                    <dt>Date and time</dt>
                    <dd>{moment(this.state.openedEnrollmentLog.createdAt).format('DD-MM-YYYY · HH:mm')}</dd>
                  </div>
                  <div>
                    <dt>School</dt>
                    <dd>{this.getEnrollmentLogSchool(this.state.openedEnrollmentLog)}</dd>
                  </div>
                  <div>
                    <dt>Affected team</dt>
                    <dd>{this.getEnrollmentLogTeam(this.state.openedEnrollmentLog)}</dd>
                  </div>
                  <div>
                    <dt>Changed by</dt>
                    <dd>
                      {this.getEnrollmentLogUser(this.state.openedEnrollmentLog)}
                      {this.state.openedEnrollmentLog.userData?.email ? (
                        <small>{this.state.openedEnrollmentLog.userData.email}</small>
                      ) : null}
                    </dd>
                  </div>
                </dl>
                <section className="utmEnrollmentLogDrawer__comment">
                  <h4>Comment</h4>
                  <textarea
                    value={this.state.enrollmentLogComment}
                    onChange={event => this.setState({ enrollmentLogComment: event.target.value })}
                    placeholder="Add a comment to this log entry"
                    rows={5}
                  />
                </section>
              </div>
              <footer>
                <button type="button" className="is-secondary" onClick={this.requestCloseEnrollmentLogDrawer}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="is-primary"
                  disabled={this.state.isEnrollmentLogCommentSaving}
                  onClick={this.saveEnrollmentLogComment}
                >
                  {this.state.isEnrollmentLogCommentSaving ? 'Saving…' : 'Save comment'}
                </button>
              </footer>
            </aside>
          </div>
        ) : null}

        <UnsavedChangesModal
          isOpen={this.state.isDiscardEnrollmentLogCommentOpen}
          message="Your comment has not been saved. If you leave now, your changes will be lost."
          onKeepEditing={() => this.setState({ isDiscardEnrollmentLogCommentOpen: false })}
          onDiscard={this.discardEnrollmentLogComment}
        />

        <SimpleModal
          isOpen={this.state.isEnrollmentLogCommentOpen}
          title={this.state.enrollmentLogComment.trim() ? 'Edit comment' : 'Add comment'}
          customClass="utmEnrollmentLogCommentModal"
          dialogStyle={{ maxWidth: 420 }}
          showFooter={false}
          isBodyBold={false}
          onCloseClick={() => this.setState({ isEnrollmentLogCommentOpen: false })}
        >
          <div className="utmEnrollmentLogCommentModal__body">
            <p>Add a short internal note to the selected enrolment log entry.</p>
            <label>
              <span>Comment</span>
              <textarea
                value={this.state.enrollmentLogComment}
                onChange={event => this.setState({ enrollmentLogComment: event.target.value })}
                placeholder="Add a note to this enrolment log entry"
                rows={4}
              />
            </label>
          </div>
          <div className="utmEnrollmentLogCommentModal__actions">
            <button type="button" onClick={() => this.setState({ isEnrollmentLogCommentOpen: false })}>
              Cancel
            </button>
            <button
              type="button"
              disabled={this.state.isEnrollmentLogCommentSaving}
              onClick={this.saveEnrollmentLogComment}
            >
              {this.state.isEnrollmentLogCommentSaving ? 'Saving…' : 'Save comment'}
            </button>
          </div>
        </SimpleModal>
      </div>
    );
  }

  renderEligibleSchools() {
    const { user } = this.props;
    const { eligibleSchools, eligiblePage, eligibleSearch, eligibleSearchDraft, selectedSchoolIds } = this.state;
    const total = this.state.counts.eligible;
    const pageIds = eligibleSchools.map(school => school.id);
    const isPageSelected = pageIds.length > 0 && pageIds.every(id => selectedSchoolIds.includes(id));
    const selectedSchools = this.state.selectedSchools;
    const canSendInvites = selectedSchools.every(
      school =>
        !school.isParticipant &&
        (!school.invite || ![INVITE_STATUS.ACCEPTED, INVITE_STATUS.REJECTED].includes(school.invite.status))
    );
    const allNeutral = selectedSchools.length > 0 && selectedSchools.every(school => !school.isParticipant && !school.isWaitingList);
    const allWaiting = selectedSchools.length > 0 && selectedSchools.every(school => school.isWaitingList);
    const allParticipants = selectedSchools.length > 0 && selectedSchools.every(school => school.isParticipant);

    return (
      <div className="utmEntries__content">
        <article className="utmEntries__panel utmEligible unionTournamentsModern">
          <header>
            <div>
              <h2>
                Eligible schools <span className="utmEntries__countChip">{total}</span>
              </h2>
            </div>
            <div className="gridModernSearch">
              <form onSubmit={this.onEligibleSearch}>
                <input
                  value={eligibleSearchDraft}
                  onChange={event => this.setState({ eligibleSearchDraft: event.target.value })}
                  placeholder="Search schools"
                />
              </form>
            </div>
            <div className="gridModernFiltersAnchor">
              <button
                type="button"
                className={`gridModernFilterButton ${this.getAppliedFiltersCount() ? 'is-active' : ''}`}
                onClick={() =>
                  this.setState(state => ({
                    isFiltersOpen: true,
                    eligibleFilterDraft: {
                      ...state.filters,
                      ages: [...state.filters.ages],
                      genders: [...state.filters.genders],
                      statuses: [...state.filters.statuses],
                      areas: [...state.filters.areas]
                    }
                  }))
                }
              >
                <span aria-hidden="true" /> Filters
                {this.getAppliedFiltersCount() ? <b>{this.getAppliedFiltersCount()}</b> : null}
              </button>
            </div>
            <div className="gridModernColumns">
              <button type="button" onClick={() => this.setState({ isColumnsOpen: true })}>
                <span aria-hidden="true" /> Columns <b>{this.state.visibleColumns.length}</b>
              </button>
            </div>
          </header>

          {selectedSchoolIds.length ? (
            <div className="utmEligible__selection">
              <strong>Selected {selectedSchoolIds.length}</strong>
              <button type="button" onClick={() => this.props.onNotifySchools(selectedSchools)}>
                Send notifications
              </button>
              {canSendInvites ? (
                <button type="button" onClick={this.openInviteConfirmation}>
                  Send invites
                </button>
              ) : null}
              {allNeutral ? (
                <button type="button" onClick={this.openAddConfirmation}>
                  Add to participants
                </button>
              ) : null}
              {allNeutral ? (
                <button type="button" onClick={this.openWaitingListConfirmation}>
                  Add to waiting list
                </button>
              ) : null}
              {allWaiting ? (
                <button
                  type="button"
                  onClick={() =>
                    this.setState(
                      {
                        selectedWaitingIds: selectedSchools.map(school => school.id),
                        selectedWaitingItems: selectedSchools as any
                      },
                      this.openMoveWaitingConfirmation
                    )
                  }
                >
                  Move to participants
                </button>
              ) : null}
              {allWaiting ? (
                <button type="button" className="is-danger" onClick={() => this.setState({
                  selectedWaitingIds: selectedSchools.map(school => school.id),
                  selectedWaitingItems: selectedSchools as any,
                  isRemoveWaitingOpen: true,
                  removeFromWaitingError: ''
                })}>Remove from waiting list</button>
              ) : null}
              {allParticipants ? (
                <button type="button" className="is-danger" onClick={this.openRemoveConfirmation}>
                  Remove from participants
                </button>
              ) : null}
              {allParticipants ? (
                <button type="button" onClick={() => this.setState({
                  selectedParticipantIds: selectedSchools.map(school => school.participantId as string),
                  selectedParticipantItems: selectedSchools.map(school => ({...school, schoolId: school.id, school: school})),
                  isMoveParticipantsOpen: true,
                  moveParticipantsError: ''
                })}>Move to waiting list</button>
              ) : null}
              <button
                className="is-clearSelection"
                type="button"
                onClick={() => this.setState({ selectedSchoolIds: [], selectedSchools: [] })}
              >
                <span aria-hidden="true">×</span> Clear selection
              </button>
            </div>
          ) : null}

          {this.renderEligibleCriteria()}

          <div className="utmEligible__tableWrap">
            <table>
              <thead>
                <tr>
                  <th>
                    <SelectAllCheckbox
                      compact
                      isChecked={isPageSelected}
                      totalCount={total}
                      currentPage={eligiblePage}
                      pageItemsCount={eligibleSchools.length}
                      selectedItems={selectedSchools}
                      selectedItemsCount={selectedSchoolIds.length}
                      onSelectAllOnPageClick={this.toggleEligiblePage}
                      onSelectAllClick={this.selectAllEligibleSchools}
                      onUnselectAllClick={() => this.setState({ selectedSchoolIds: [], selectedSchools: [] })}
                    />
                  </th>
                  {this.state.visibleColumns.includes('school') ? <th>School</th> : null}
                  {this.state.visibleColumns.includes('ages') ? <th>Available ages</th> : null}
                  {this.state.visibleColumns.includes('gender') ? <th>Available gender</th> : null}
                  {this.state.visibleColumns.includes('participant') ? <th>Participant</th> : null}
                  {this.state.visibleColumns.includes('status') ? <th>Invite status</th> : null}
                  {this.state.visibleColumns.includes('date') ? <th>Invite updated</th> : null}
                  {this.state.visibleColumns.includes('area') ? <th>Area</th> : null}
                </tr>
              </thead>
              <tbody>
                {eligibleSchools.map(school => {
                  const inviteStatus = getTournamentInviteStatus(school);
                  return (
                    <tr key={school.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedSchoolIds.includes(school.id)}
                          onChange={() => this.toggleSchool(school)}
                        />
                      </td>
                      {this.state.visibleColumns.includes('school') ? (
                        <td>
                          <strong>{school.name}</strong>
                        </td>
                      ) : null}
                      {this.state.visibleColumns.includes('ages') ? (
                        <td>{getSchoolAvailableAgesGroup(school as any, { user }) || '—'}</td>
                      ) : null}
                      {this.state.visibleColumns.includes('gender') ? (
                        <td>{getSchoolAvailableGender(school as any) || '—'}</td>
                      ) : null}
                      {this.state.visibleColumns.includes('participant') ? (
                        <td>
                          <span
                            className={school.isParticipant ? 'utmEligible__chip is-yes' : 'utmEligible__chip is-no'}
                          >
                            {school.isParticipant ? 'Yes' : 'No'}
                          </span>
                        </td>
                      ) : null}
                      {this.state.visibleColumns.includes('status') ? (
                        <td>
                          <span
                            className={`utmEligible__chip is-${String(inviteStatus || 'not-sent')
                              .toLowerCase()
                              .replace(/[^a-z]+/g, '-')}`}
                          >
                            {inviteStatus || 'Not sent'}
                          </span>
                        </td>
                      ) : null}
                      {this.state.visibleColumns.includes('date') ? (
                        <td>
                          {school.invite?.statusSetAt
                            ? moment(school.invite.statusSetAt).format('DD-MM-YYYY HH:mm')
                            : '—'}
                        </td>
                      ) : null}
                      {this.state.visibleColumns.includes('area') ? (
                        <td>{this.getSchoolAreas(school) || '—'}</td>
                      ) : null}
                    </tr>
                  );
                })}
                {!this.state.isEligibleLoading && eligibleSchools.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="utmEligible__empty">
                      <TableEmptyState
                        filtered={Boolean(eligibleSearch || this.state.filters.ages.length || this.state.filters.genders.length || this.state.filters.participant || this.state.filters.statuses.length || this.state.filters.dateFrom || this.state.filters.dateTo || this.state.filters.areas.length)}
                        emptyTitle="No eligible schools yet"
                        emptyText="Schools that meet this tournament’s eligibility rules will appear here."
                        filteredTitle="No eligible schools match your filters"
                        filteredText="Try broadening the eligibility filters or clearing the search."
                        icon="M3 21v-8h6v8M15 21V9h6v12M5 9V3h14v4M8 6h2M14 6h2"
                      />
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <Pagination2
            modern
            currentPage={eligiblePage}
            total={total}
            limit={20}
            setCurrentPageParams={page => this.loadEligibleSchools(page, eligibleSearch)}
          />
        </article>
        {this.renderEligibleFilters()}
        {this.renderColumnsModal()}
        {this.renderWaitingListConfirmation()}
        {this.renderInviteConfirmation()}
        {this.renderAddConfirmation()}
        {this.renderParticipantLimitWarning()}
        {this.renderRemoveConfirmation()}
        <SimpleModal
          isOpen={this.state.isMoveParticipantsOpen}
          title="Move participants to waiting list?"
          buttonText={this.state.isMovingParticipants ? 'Moving…' : 'Move to waiting list'}
          onButtonClick={this.moveParticipantsToWaiting}
          onCloseClick={() => !this.state.isMovingParticipants && this.setState({ isMoveParticipantsOpen: false })}
          customClass="unionTournamentDeleteModal"
          isBodyBold={false}
        >
          <div className="unionTournamentDeleteModal__body">
            <div className="unionTournamentDeleteModal__icon" aria-hidden="true"><Icon path="M12 8v5M12 17h.01M10.3 3.9 2.4 17.6A2 2 0 0 0 4.1 20h15.8a2 2 0 0 0 1.7-2.4L13.7 3.9a2 2 0 0 0-3.4 0Z" /></div>
            <div className="unionTournamentDeleteModal__copy">
              <strong>{this.state.selectedParticipantItems.length} selected school{this.state.selectedParticipantItems.length === 1 ? '' : 's'}</strong>
              <p>The schools’ tournament teams will be deleted and their queue positions will start from the end of the waiting list.</p>
              <p>This action is blocked while a school is used in a competition group or fixture.</p>
              {this.state.moveParticipantsError ? <div className="utmEligible__removeError">{this.state.moveParticipantsError}</div> : null}
            </div>
          </div>
        </SimpleModal>
        <SimpleModal
          isOpen={this.state.isRemoveWaitingOpen}
          title="Remove schools from waiting list?"
          buttonText={this.state.isRemovingFromWaiting ? 'Removing…' : 'Remove from waiting list'}
          onButtonClick={this.removeSelectedFromWaitingList}
          onCloseClick={() => !this.state.isRemovingFromWaiting && this.setState({ isRemoveWaitingOpen: false })}
          customClass="unionTournamentDeleteModal"
          isBodyBold={false}
        >
          <div className="unionTournamentDeleteModal__body">
            <div className="unionTournamentDeleteModal__icon" aria-hidden="true"><Icon path="M12 8v5M12 17h.01M10.3 3.9 2.4 17.6A2 2 0 0 0 4.1 20h15.8a2 2 0 0 0 1.7-2.4L13.7 3.9a2 2 0 0 0-3.4 0Z" /></div>
            <div className="unionTournamentDeleteModal__copy"><strong>{this.state.selectedWaitingItems.length} selected school{this.state.selectedWaitingItems.length === 1 ? '' : 's'}</strong><p>They will lose their queue positions and their tournament invitations will be marked as rejected.</p></div>
          </div>
        </SimpleModal>
        <SimpleModal
          isOpen={this.state.isMoveWaitingOpen}
          title="Move schools to participants?"
          buttonText={this.state.isMovingWaiting ? 'Moving…' : 'Move to participants'}
          buttonDisabled={this.state.isParticipantEventsLoading || Boolean(this.state.moveWaitingError)}
          onButtonClick={() => this.moveWaitingToParticipants(Boolean(this.state.moveWaitingCapacityWarning))}
          onCloseClick={() => !this.state.isMovingWaiting && this.setState({ isMoveWaitingOpen: false })}
          isBodyBold={false}
        >
          <p><strong>{this.state.selectedWaitingItems.length} selected school{this.state.selectedWaitingItems.length === 1 ? '' : 's'}</strong></p>
          <p>They will leave the queue, become participants and receive their default tournament teams.</p>
          {this.renderMoveWaitingEventSelection()}
          {this.state.moveWaitingCapacityWarning ? <div className="utmWaitingMoveModal__capacity">{this.state.moveWaitingCapacityWarning}</div> : null}
          {this.state.moveWaitingError ? <div className="utmEligible__removeError">{this.state.moveWaitingError}</div> : null}
        </SimpleModal>
      </div>
    );
  }

  renderParticipants() {
    const {
      participants,
      participantsPage,
      participantsSearch,
      participantsSearchDraft,
      isParticipantsLoading
    } = this.state;
    const total = this.state.counts.participants;
    const hasAnyParticipants = (this.state.participantsUnfilteredTotal ?? total) > 0;
    const selectParticipantsOnPage = () => {
      this.setState(state => {
        const byId = new Map(state.selectedParticipantItems.map(item => [String(item.id), item]));
        state.participants.forEach(item => byId.set(String(item.id), item));
        const selectedParticipantItems = Array.from(byId.values());
        return {
          selectedParticipantItems,
          selectedParticipantIds: selectedParticipantItems.map(item => item.id)
        } as Pick<State, 'selectedParticipantItems' | 'selectedParticipantIds'>;
      });
    };
    const selectAllParticipants = () => {
      const where: any = {};
      if (participantsSearch) where.name = { like: participantsSearch, options: 'i' };
      this.setState({ isParticipantsLoading: true });
      getTournamentParticipants(this.props.user, this.props.tournament.id, {
        where,
        limit: Math.max(total, 20),
        skip: 0,
        order: 'createdAt DESC'
      } as any).then(allParticipants => {
        this.setState({
          selectedParticipantItems: allParticipants,
          selectedParticipantIds: allParticipants.map(item => item.id),
          isParticipantsLoading: false
        });
      }).catch(() => this.setState({ isParticipantsLoading: false }));
    };

    const getTeamCount = (participant: any) => {
      const teams = participant.teams || participant.tournamentTeams || participant.schoolTeams;
      if (Array.isArray(teams)) return teams.length;
      const value = Number(participant.teamsCount ?? participant.teamCount ?? participant.numberOfTeams);
      if (isFinite(value)) return value;
      return this.state.participantTeamCounts[String(participant.schoolId)] || 0;
    };
    const getEnteredAt = (participant: any, entryMeta?: any) => {
      const explicit = entryMeta?.createdAt || participant.enteredAt || participant.createdAt;
      if (explicit) return moment(explicit);
      const id = String(participant.id || '');
      if (/^[a-f\d]{24}$/i.test(id)) return moment(parseInt(id.slice(0, 8), 16) * 1000);
      return null;
    };
    const getParticipantAreas = (participant: any) => {
      if (Array.isArray(participant.areaNames) && participant.areaNames.length) {
        return participant.areaNames.join(', ');
      }
      const schoolId = String(participant.schoolId || participant.school?.id || '');
      const names = this.state.areas
        .filter(area => (area.schoolIds || []).some(areaSchoolId => String(areaSchoolId) === schoolId))
        .map(area => area.name)
        .filter(Boolean);
      return names.length ? names.join(', ') : '—';
    };
    const getEntryLabel = (participant: any, entryMeta?: any) => {
      const raw = String(
        entryMeta?.action ||
          participant.howEntered ||
          participant.entrySource ||
          participant.invite?.status ||
          participant.inviteStatus ||
          participant.status ||
          ''
      ).toUpperCase();
      if (raw === 'ACCEPTED') return 'Accepted';
      if (raw === 'PREBOOKED' || raw === 'NOT_READY' || raw === 'PENDING') return 'Awaiting response';
      if (raw === 'MANUAL' || raw === 'ADDED' || raw === 'SCHOOL_ADDED' || raw === 'SCHOOL_MANUALLY_ENROLLED') {
        return 'Added manually';
      }
      if (raw === 'AUTO' || raw === 'AUTO_ENROLLED' || raw === 'SCHOOL_AUTO_ENROLLED') return 'Auto-enrolled';
      return raw
        ? raw.charAt(0) +
            raw
              .slice(1)
              .toLowerCase()
              .replace(/_/g, ' ')
        : 'Added manually';
    };

    return (
      <div className="utmEntries__content">
        <article className="utmEntries__panel utmParticipants unionTournamentsModern">
          <header>
            <div className="utmParticipants__heading">
              <h2>
                Participants <b>{total}</b>
              </h2>
              <div className="gridModernSearch">
                <form onSubmit={this.onParticipantsSearch}>
                  <input
                    value={participantsSearchDraft}
                    onChange={event => this.setState({ participantsSearchDraft: event.target.value })}
                    placeholder="Search participants"
                  />
                </form>
              </div>
            </div>
            <button
              type="button"
              className="utmParticipants__manage"
              onClick={() => this.props.onOpenLegacySection('participantsLegacy')}
            >
              <Icon path="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M19 8v6M22 11h-6" />
              Manage participants
            </button>
          </header>

          {participantsSearch ? (
            <div className="utmEligible__criteria">
              <span>
                School: {participantsSearch}
                <button
                  type="button"
                  aria-label="Clear participant search"
                  onClick={() =>
                    this.setState({ participantsSearch: '', participantsSearchDraft: '' }, () =>
                      this.loadParticipants(1, '')
                    )
                  }
                >
                  ×
                </button>
              </span>
            </div>
          ) : null}

          {this.state.selectedParticipantIds.length ? (
            <div className="utmEligible__selection">
              <strong>Selected {this.state.selectedParticipantIds.length}</strong>
              <button
                type="button"
                onClick={() => this.setState({ isMoveParticipantsOpen: true, moveParticipantsError: '' })}
              >
                Move to waiting list
              </button>
              <button type="button" className="is-danger" onClick={() => this.setState({
                selectedSchoolIds: this.state.selectedParticipantItems.map(item => String(item.schoolId || item.school?.id)),
                selectedSchools: this.state.selectedParticipantItems.map(item => ({
                  ...item.school,
                  id: String(item.schoolId || item.school?.id),
                  name: item.school?.name || item.schoolName,
                  isParticipant: true,
                  participantId: item.id
                })),
                isRemoveConfirmationOpen: true,
                removeParticipantsError: ''
              })}>Remove from participants</button>
              <button type="button" onClick={() => this.props.onNotifySchools(this.state.selectedParticipantItems.map(item => ({
                ...item.school,
                id: String(item.schoolId || item.school?.id),
                name: item.school?.name || item.schoolName
              })))}>Send notifications</button>
              <button
                className="is-clearSelection"
                type="button"
                onClick={() => this.setState({ selectedParticipantIds: [], selectedParticipantItems: [] })}
              >
                <span aria-hidden="true">×</span> Clear selection
              </button>
            </div>
          ) : null}

          <div className="utmParticipants__tableWrap">
            <table>
              <thead>
                <tr>
                  <th>
                    <SelectAllCheckbox
                      compact
                      onSelectAllClick={selectAllParticipants}
                      onSelectAllOnPageClick={selectParticipantsOnPage}
                      onUnselectAllClick={() => this.setState({ selectedParticipantIds: [], selectedParticipantItems: [] })}
                      isChecked={total > 0 && this.state.selectedParticipantIds.length >= total}
                      totalCount={total}
                      currentPage={participantsPage}
                      selectedItems={this.state.selectedParticipantItems}
                      pageItemsCount={participants.length}
                    />
                  </th>
                  <th>School</th>
                  <th>Teams</th>
                  <th>How it entered</th>
                  <th>Entered</th>
                  <th>Area</th>
                </tr>
              </thead>
              <tbody>
                {isParticipantsLoading ? (
                  <tr>
                    <td colSpan={6} className="utmEligible__empty">
                      Loading participants…
                    </td>
                  </tr>
                ) : null}
                {participants.map(participant => {
                  const schoolId = String(participant.schoolId || participant.school?.id || '');
                  const entryMeta = this.state.participantEntryMeta[schoolId];
                  const entryLabel = getEntryLabel(participant, entryMeta);
                  const enteredAt = getEnteredAt(participant, entryMeta);
                  const enteredBy = [entryMeta?.userData?.firstName, entryMeta?.userData?.lastName]
                    .filter(Boolean)
                    .join(' ');
                  const enteredByLabel = enteredBy || entryMeta?.schoolData?.name || '';
                  const schoolName = participant.school?.name || participant.schoolName || 'Unknown school';
                  const schoolPic = participant.school?.pic || participant.pic || participant.schoolPic;
                  const area =
                    participant.area?.name ||
                    participant.areaName ||
                    participant.school?.area?.name ||
                    participant.school?.areaName ||
                    getParticipantAreas(participant);
                  return (
                    <tr key={participant.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={this.state.selectedParticipantIds.includes(participant.id)}
                          onChange={() => this.toggleParticipant(participant)}
                        />
                      </td>
                      <td>
                        <div className="utmParticipants__school">
                          <span className="utmParticipants__schoolLogo">
                            <img src={schoolPic || TOURNAMENT_SCHOOL_LOGO_FALLBACK} alt="" />
                          </span>
                          <strong>{schoolName}</strong>
                        </div>
                      </td>
                      <td>
                        <strong>{getTeamCount(participant) || '—'}</strong>
                      </td>
                      <td>
                        <span className={`utmEligible__chip is-${entryLabel.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                          {entryLabel}
                        </span>
                        {enteredByLabel ? (
                          <small className="utmParticipants__enteredBy">by {enteredByLabel}</small>
                        ) : null}
                      </td>
                      <td>{enteredAt ? enteredAt.format('DD-MM-YYYY HH:mm') : '—'}</td>
                      <td>{area}</td>
                    </tr>
                  );
                })}
                {!isParticipantsLoading && participants.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="utmEligible__empty">
                      <div className="utmTableEmptyState">
                        <span aria-hidden="true">
                          <Icon
                            path={
                              participantsSearch && hasAnyParticipants
                                ? 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16 16l4 4'
                                : 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M19 8v6M22 11h-6'
                            }
                          />
                        </span>
                        <strong>
                          {participantsSearch && hasAnyParticipants
                            ? 'No participants match your search'
                            : 'No participants yet'}
                        </strong>
                        <p>
                          {participantsSearch && hasAnyParticipants
                            ? 'Try a different school name or clear the search.'
                            : 'Participants will appear here when schools join the competition.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <Pagination2
            modern
            currentPage={participantsPage}
            total={total}
            limit={20}
            setCurrentPageParams={page => this.loadParticipants(page, participantsSearch)}
          />
        </article>
        <SimpleModal
          isOpen={this.state.isMoveParticipantsOpen}
          title="Move participants to waiting list?"
          buttonText={this.state.isMovingParticipants ? 'Moving…' : 'Move to waiting list'}
          onButtonClick={this.moveParticipantsToWaiting}
          onCloseClick={() =>
            !this.state.isMovingParticipants &&
            this.setState({ isMoveParticipantsOpen: false, moveParticipantsError: '' })
          }
          customClass="unionTournamentDeleteModal"
          dialogStyle={{ maxWidth: 520 }}
          isBodyBold={false}
        >
          <div className="unionTournamentDeleteModal__body">
            <div className="unionTournamentDeleteModal__icon" aria-hidden="true">
              <Icon path="M12 8v5M12 17h.01M10.3 3.9 2.4 17.6A2 2 0 0 0 4.1 20h15.8a2 2 0 0 0 1.7-2.4L13.7 3.9a2 2 0 0 0-3.4 0Z" />
            </div>
            <div className="unionTournamentDeleteModal__copy">
              <strong>
                {this.state.selectedParticipantItems.length} selected school
                {this.state.selectedParticipantItems.length === 1 ? '' : 's'}
              </strong>
              <p>
                Their tournament teams will be deleted and their queue positions will start from the end of the
                waiting list.
              </p>
              <p>This action is blocked while a school is used in a competition group or fixture.</p>
              {this.state.moveParticipantsError ? (
                <div className="utmEligible__removeError">{this.state.moveParticipantsError}</div>
              ) : null}
            </div>
          </div>
        </SimpleModal>
        {this.renderRemoveConfirmation()}
      </div>
    );
  }

  renderWaitingList() {
    const { waitingItems, waitingPage, waitingSearch, waitingSearchDraft, isWaitingLoading } = this.state;
    const total = this.state.counts.waiting;
    const isPageSelected =
      waitingItems.length > 0 && waitingItems.every(item => this.state.selectedWaitingIds.includes(item.id));
    const getWaitingSource = (item: TournamentWaitingListItem, entryMeta?: any) => {
      if (item.invite?.isAutoAccepted) return { label: 'Auto-accepted', className: 'is-auto' };
      if (entryMeta?.action === TOURNAMENT_LOG_ACTION_TYPES.INVITE_ACCEPTED) {
        return { label: 'Invite accepted', className: 'is-invite' };
      }
      if ((item.invite as any)?.inviteSource === 'ONLINE_BOOKING') {
        return { label: 'Online booking', className: 'is-invite' };
      }
      if (entryMeta?.action === TOURNAMENT_LOG_ACTION_TYPES.SCHOOL_ADDED_TO_WAITING_LIST) {
        return { label: 'Added manually', className: 'is-manual' };
      }
      return { label: 'Waiting list', className: 'is-neutral' };
    };
    const getArea = (item: TournamentWaitingListItem) => {
      if (Array.isArray((item as any).areaNames) && (item as any).areaNames.length) {
        return (item as any).areaNames.join(', ');
      }
      const schoolId = String(item.id);
      const names = this.state.areas
        .filter(area => (area.schoolIds || []).some(areaSchoolId => String(areaSchoolId) === schoolId))
        .map(area => area.name)
        .filter(Boolean);
      return names.length ? names.join(', ') : '—';
    };

    return (
      <div className="utmEntries__content">
        <article className="utmEntries__panel utmWaitingList unionTournamentsModern">
          <header>
            <div className="utmWaitingList__heading">
              <h2>
                Waiting list <b>{total}</b>
              </h2>
              {total > 0 || waitingSearch ? (
                <div className="gridModernSearch utmWaitingList__search">
                  <form onSubmit={this.onWaitingSearch}>
                    <input
                      value={waitingSearchDraft}
                      onChange={event => this.setState({ waitingSearchDraft: event.target.value })}
                      placeholder="Search schools"
                    />
                  </form>
                </div>
              ) : null}
            </div>
          </header>

          {waitingSearch ? (
            <div className="utmEligible__criteria">
              <span>
                School: {waitingSearch}
                <button
                  type="button"
                  aria-label="Clear waiting list search"
                  onClick={() =>
                    this.setState({ waitingSearch: '', waitingSearchDraft: '' }, () => this.loadWaitingList(1, ''))
                  }
                >
                  ×
                </button>
              </span>
            </div>
          ) : null}

          {this.state.selectedWaitingIds.length ? (
            <div className="utmEligible__selection">
              <strong>Selected {this.state.selectedWaitingIds.length}</strong>
              <button
                type="button"
                onClick={this.openMoveWaitingConfirmation}
              >
                Move to participants
              </button>
              <button
                type="button"
                className="is-danger"
                onClick={() => this.setState({ isRemoveWaitingOpen: true, removeFromWaitingError: '' })}
              >
                Remove from waiting list
              </button>
              <button type="button" onClick={() => this.props.onNotifySchools(this.state.selectedWaitingItems.map(item => ({
                ...item,
                isParticipant: false,
                isWaitingList: true
              } as EligibleSchool)))}>Send notifications</button>
              <button
                className="is-clearSelection"
                type="button"
                onClick={() => this.setState({ selectedWaitingIds: [], selectedWaitingItems: [] })}
              >
                <span aria-hidden="true">×</span> Clear selection
              </button>
            </div>
          ) : null}

          {!isWaitingLoading && total === 0 && !waitingSearch ? (
            <div className="utmWaitingList__empty">
              <span aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M7 3h10M7 21h10M8 3c0 5 1.5 6.5 4 9-2.5 2.5-4 4-4 9M16 3c0 5-1.5 6.5-4 9 2.5 2.5 4 4 4 9" />
                </svg>
              </span>
              <strong>No schools waiting</strong>
              <p>Places are still available, so schools enter directly. The queue fills once capacity is reached.</p>
            </div>
          ) : (
            <>
              <div className="utmWaitingList__tableWrap">
                <table>
                  <thead>
                    <tr>
                      <th>
                        <SelectAllCheckbox
                          compact
                          isChecked={isPageSelected}
                          totalCount={total}
                          currentPage={waitingPage}
                          pageItemsCount={waitingItems.length}
                          selectedItems={this.state.selectedWaitingItems}
                          selectedItemsCount={this.state.selectedWaitingIds.length}
                          onSelectAllOnPageClick={this.selectWaitingPage}
                          onSelectAllClick={this.selectAllWaiting}
                          onUnselectAllClick={() => this.setState({ selectedWaitingIds: [], selectedWaitingItems: [] })}
                        />
                      </th>
                      <th>#</th>
                      <th>School</th>
                      <th>Teams requested</th>
                      <th>How joined</th>
                      <th>Added by</th>
                      <th>Joined queue</th>
                      <th>Area</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isWaitingLoading ? (
                      <tr>
                        <td colSpan={8} className="utmEligible__empty">
                          Loading waiting list…
                        </td>
                      </tr>
                    ) : null}
                    {waitingItems.map((item, index) => {
                      const entryMeta = this.state.waitingEntryMeta[String(item.id)];
                      const source = getWaitingSource(item, entryMeta);
                      const userName = [entryMeta?.userData?.firstName, entryMeta?.userData?.lastName]
                        .filter(Boolean)
                        .join(' ');
                      const isBackfilledEntry = String(entryMeta?.comment || '').startsWith(
                        'Backfilled historical waiting-list entry'
                      );
                      const actor =
                        userName || (isBackfilledEntry ? 'Historical entry' : entryMeta?.schoolData?.name) || '—';
                      const joinedAt = entryMeta?.createdAt || item.waitingListJoinedAt || item.invite?.statusSetAt;
                      return (
                        <tr key={item.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={this.state.selectedWaitingIds.includes(item.id)}
                              onChange={() => this.toggleWaitingItem(item)}
                            />
                          </td>
                          <td>
                            <strong>{item.waitingListPosition || (waitingPage - 1) * 20 + index + 1}</strong>
                          </td>
                          <td>
                            <div className="utmWaitingList__school">
                              <span aria-hidden="true">
                                <img src={item.pic || TOURNAMENT_SCHOOL_LOGO_FALLBACK} alt="" />
                              </span>
                              <strong>{item.name}</strong>
                            </div>
                          </td>
                          <td>{Number((item as any).teamsRequested) || '—'}</td>
                          <td>
                            <span className={`utmWaitingList__source ${source.className}`}>{source.label}</span>
                          </td>
                          <td>
                            <strong className="utmWaitingList__actor">{actor}</strong>
                            {userName && entryMeta?.schoolData?.name ? (
                              <small>{entryMeta.schoolData.name}</small>
                            ) : null}
                          </td>
                          <td>{joinedAt ? moment(joinedAt).format('DD-MM-YYYY HH:mm') : '—'}</td>
                          <td>{getArea(item)}</td>
                        </tr>
                      );
                    })}
                    {!isWaitingLoading && waitingItems.length === 0 && waitingSearch ? (
                      <tr>
                        <td colSpan={8} className="utmEligible__empty">
                          <TableEmptyState
                            filtered
                            emptyTitle="No schools waiting"
                            emptyText="Schools will appear here when they join the tournament queue."
                            filteredTitle="No waiting-list schools match your search"
                            filteredText="Try a different school name or clear the search."
                            icon="M7 3h10M7 21h10M8 3c0 5 1.5 6.5 4 9-2.5 2.5-4 4-4 9M16 3c0 5-1.5 6.5-4 9 2.5 2.5 4 4 4 9"
                          />
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              <Pagination2
                modern
                currentPage={waitingPage}
                total={total}
                limit={20}
                setCurrentPageParams={page => this.loadWaitingList(page, waitingSearch)}
              />
            </>
          )}
        </article>
        <SimpleModal
          isOpen={this.state.isRemoveWaitingOpen}
          isBare
          onBackdropClick={() =>
            !this.state.isRemovingFromWaiting &&
            this.setState({ isRemoveWaitingOpen: false, removeFromWaitingError: '' })
          }
        >
          <section
            className="utmWaitingRemoveModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-waiting-title"
          >
            <header>
              <div>
                <h2 id="remove-waiting-title">Remove from waiting list?</h2>
                <p>Review the schools before confirming.</p>
              </div>
              <button
                type="button"
                aria-label="Close"
                disabled={this.state.isRemovingFromWaiting}
                onClick={() => this.setState({ isRemoveWaitingOpen: false, removeFromWaitingError: '' })}
              >
                ×
              </button>
            </header>
            <div className="utmWaitingRemoveModal__body">
              <div className="utmWaitingRemoveModal__summary">
                <span>Schools selected</span>
                <b>{this.state.selectedWaitingItems.length}</b>
              </div>
              <div className="utmWaitingRemoveModal__schools">
                {this.state.selectedWaitingItems.map(item => (
                  <span key={item.id}>{item.name}</span>
                ))}
              </div>
              <div className="utmWaitingRemoveModal__notice">
                These schools will lose their queue positions and will no longer be offered the next available place.
                Their tournament invitations will be marked as rejected. You can add them again later.
              </div>
              {this.state.removeFromWaitingError ? (
                <div className="utmEligible__removeError">{this.state.removeFromWaitingError}</div>
              ) : null}
            </div>
            <footer>
              <button
                type="button"
                disabled={this.state.isRemovingFromWaiting}
                onClick={() => this.setState({ isRemoveWaitingOpen: false, removeFromWaitingError: '' })}
              >
                Cancel
              </button>
              <button
                type="button"
                className="is-danger"
                disabled={this.state.isRemovingFromWaiting}
                onClick={this.removeSelectedFromWaitingList}
              >
                {this.state.isRemovingFromWaiting
                  ? 'Removing…'
                  : this.state.selectedWaitingItems.length === 1
                  ? 'Remove school'
                  : 'Remove schools'}
              </button>
            </footer>
          </section>
        </SimpleModal>
        <SimpleModal
          isOpen={this.state.isMoveWaitingOpen}
          isBare
          onBackdropClick={() =>
            !this.state.isMovingWaiting &&
            this.setState({ isMoveWaitingOpen: false, moveWaitingError: '', moveWaitingCapacityWarning: '' })
          }
        >
          <section
            className="utmWaitingRemoveModal utmWaitingMoveModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="move-waiting-title"
          >
            <header>
              <div>
                <h2 id="move-waiting-title">
                  {this.state.moveWaitingCapacityWarning
                    ? isIndividualTournament(this.props.tournament)
                      ? 'Participant limit will be exceeded'
                      : 'Team limit will be exceeded'
                    : 'Move to participants?'}
                </h2>
                <p>Review the schools before confirming.</p>
              </div>
              <button
                type="button"
                aria-label="Close"
                disabled={this.state.isMovingWaiting}
                onClick={() =>
                  this.setState({ isMoveWaitingOpen: false, moveWaitingError: '', moveWaitingCapacityWarning: '' })
                }
              >
                ×
              </button>
            </header>
            <div className="utmWaitingRemoveModal__body">
              <div className="utmWaitingRemoveModal__summary">
                <span>Schools selected</span>
                <b>{this.state.selectedWaitingItems.length}</b>
              </div>
              <div className="utmWaitingRemoveModal__schools">
                {this.state.selectedWaitingItems.map(item => (
                  <span key={item.id}>{item.name}</span>
                ))}
              </div>
              <div className="utmWaitingRemoveModal__notice utmWaitingMoveModal__notice">
                {this.state.selectedWaitingItems.length === 1 ? 'This school' : 'These schools'} will leave the waiting
                list, become tournament participants and receive default teams.
              </div>
              {this.renderMoveWaitingEventSelection()}
              {this.state.moveWaitingCapacityWarning ? (
                <div className="utmWaitingMoveModal__capacity">
                  <strong>Capacity warning</strong>
                  <span>{this.state.moveWaitingCapacityWarning}</span>
                </div>
              ) : null}
              {this.state.moveWaitingError ? (
                <div className="utmEligible__removeError">{this.state.moveWaitingError}</div>
              ) : null}
            </div>
            <footer>
              <button
                type="button"
                disabled={this.state.isMovingWaiting}
                onClick={() =>
                  this.setState({ isMoveWaitingOpen: false, moveWaitingError: '', moveWaitingCapacityWarning: '' })
                }
              >
                Cancel
              </button>
              <button
                type="button"
                className="is-primary"
                disabled={
                  this.state.isMovingWaiting ||
                  this.state.isParticipantEventsLoading ||
                  Boolean(this.state.moveWaitingError)
                }
                onClick={() => this.moveWaitingToParticipants(Boolean(this.state.moveWaitingCapacityWarning))}
              >
                {this.state.isMovingWaiting
                  ? 'Moving…'
                  : this.state.moveWaitingCapacityWarning
                  ? 'Move anyway'
                  : this.state.selectedWaitingItems.length === 1
                  ? 'Move school'
                  : 'Move schools'}
              </button>
            </footer>
          </section>
        </SimpleModal>
      </div>
    );
  }

  renderConfirmationRequests() {
    const {
      confirmationRequests,
      confirmationPage,
      confirmationSearch,
      confirmationSearchDraft,
      confirmationStatus,
      confirmationStatusDraft,
      isConfirmationLoading,
      openedConfirmationRequest,
      confirmationVisibleColumns
    } = this.state;
    const total = this.state.counts.requests;
    const statusDetails = (status: string) => {
      switch (status) {
        case CONFIRMATION_REQUESTS_STATUS.ACCEPTED:
          return { label: 'Accepted', className: 'is-accepted' };
        case CONFIRMATION_REQUESTS_STATUS.REJECTED:
          return { label: 'Rejected', className: 'is-rejected' };
        case CONFIRMATION_REQUESTS_STATUS.OUTDATED:
          return { label: 'Outdated', className: 'is-outdated' };
        default:
          return { label: 'Awaiting response', className: 'is-pending' };
      }
    };
    const formatDate = (value: any) =>
      value && moment(value).isValid() ? moment(value).format('DD-MM-YYYY HH:mm') : '—';

    return (
      <div className="utmEntries__content">
        <article className="utmEntries__panel utmConfirmationRequests unionTournamentsModern">
          <header>
            <div className="utmConfirmationRequests__heading">
              <h2>
                Confirmation requests <b>{total}</b>
              </h2>
            </div>
            <div className="utmConfirmationRequests__headerTools">
              <div className="gridModernSearch utmConfirmationRequests__search">
                <form onSubmit={this.onConfirmationSearch}>
                  <input
                    value={confirmationSearchDraft}
                    onChange={event => this.setState({ confirmationSearchDraft: event.target.value })}
                    placeholder="Search schools"
                  />
                </form>
              </div>
              <div className="gridModernFiltersAnchor">
                <button
                  type="button"
                  className={`gridModernFilterButton ${confirmationStatus ? 'is-active' : ''}`}
                  onClick={() =>
                    this.setState({
                      isConfirmationFiltersOpen: true,
                      confirmationStatusDraft: confirmationStatus
                    })
                  }
                >
                  <span aria-hidden="true" /> Filters {confirmationStatus ? <b>1</b> : null}
                </button>
              </div>
              <div className="gridModernColumns">
                <button type="button" onClick={() => this.setState({ isConfirmationColumnsOpen: true })}>
                  <span aria-hidden="true" /> Columns <b>{confirmationVisibleColumns.length + 1}</b>
                </button>
              </div>
            </div>
            <button type="button" className="utmConfirmationRequests__send" onClick={this.openSendConfirmationRequests}>
              <Icon path="M22 2L11 13M22 2l-7 20-4-9-9-4z" />
              Send requests
            </button>
          </header>

          {confirmationSearch || confirmationStatus ? (
            <div className="utmEligible__criteria">
              {confirmationSearch ? (
                <span>
                  School: {confirmationSearch}
                  <button
                    type="button"
                    onClick={() =>
                      this.setState({ confirmationSearch: '', confirmationSearchDraft: '' }, () =>
                        this.loadConfirmationRequests(1, '', confirmationStatus)
                      )
                    }
                  >
                    ×
                  </button>
                </span>
              ) : null}
              {confirmationStatus ? (
                <span>
                  Status: {statusDetails(confirmationStatus).label}
                  <button type="button" onClick={() => this.loadConfirmationRequests(1, confirmationSearch, '')}>
                    ×
                  </button>
                </span>
              ) : null}
            </div>
          ) : null}

          <div className="utmConfirmationRequests__tableWrap">
            <table>
              <thead>
                <tr>
                  <th>School</th>
                  {confirmationVisibleColumns.includes('status') ? <th>Status</th> : null}
                  {confirmationVisibleColumns.includes('respondedBy') ? <th>Responded by</th> : null}
                  {confirmationVisibleColumns.includes('respondedAt') ? <th>Responded at</th> : null}
                  {confirmationVisibleColumns.includes('deadline') ? <th>Deadline</th> : null}
                </tr>
              </thead>
              <tbody>
                {isConfirmationLoading ? (
                  <tr>
                    <td colSpan={confirmationVisibleColumns.length + 1} className="utmEligible__empty">
                      Loading confirmation requests…
                    </td>
                  </tr>
                ) : null}
                {confirmationRequests.map(request => {
                  const status = statusDetails(request.invitationStatus);
                  const responseUser = getResponseUser(request);
                  return (
                    <tr
                      key={request.id}
                      className="utmConfirmationRequests__row"
                      tabIndex={0}
                      onClick={() => this.setState({ openedConfirmationRequest: request })}
                      onKeyDown={event =>
                        (event.key === 'Enter' || event.key === ' ') &&
                        this.setState({ openedConfirmationRequest: request })
                      }
                    >
                      <td>
                        <strong>
                          {request.participantSchoolData?.name || request.participantSchoolName || 'Unknown school'}
                        </strong>
                      </td>
                      {confirmationVisibleColumns.includes('status') ? (
                        <td>
                          <span className={`utmConfirmationRequests__status ${status.className}`}>{status.label}</span>
                        </td>
                      ) : null}
                      {confirmationVisibleColumns.includes('respondedBy') ? (
                        <td>
                          {responseUser.fullName ? <strong>{responseUser.fullName}</strong> : '—'}
                          {responseUser.email ? <small>{responseUser.email}</small> : null}
                        </td>
                      ) : null}
                      {confirmationVisibleColumns.includes('respondedAt') ? (
                        <td>{getRespondedAtText(request) || '—'}</td>
                      ) : null}
                      {confirmationVisibleColumns.includes('deadline') ? (
                        <td>{formatDate(request.deadlineForAnswers)}</td>
                      ) : null}
                    </tr>
                  );
                })}
                {!isConfirmationLoading && confirmationRequests.length === 0 ? (
                  <tr>
                    <td colSpan={confirmationVisibleColumns.length + 1} className="utmEligible__empty">
                      <TableEmptyState
                        filtered={Boolean(confirmationSearch || confirmationStatus)}
                        emptyTitle="No confirmation requests yet"
                        emptyText="Requests sent to participating schools will appear here with their response status."
                        filteredTitle="No confirmation requests match your filters"
                        filteredText="Try a different school name or clear the status filter."
                        icon="M4 5h16v14H4zM7 9h10M7 13h6M16 15l2 2 3-4"
                      />
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <Pagination2
            modern
            currentPage={confirmationPage}
            total={total}
            limit={20}
            setCurrentPageParams={page => this.loadConfirmationRequests(page, confirmationSearch, confirmationStatus)}
          />
        </article>

        {this.state.isConfirmationFiltersOpen ? (
          <div
            className="utmEligiblePanelBackdrop"
            onMouseDown={() => this.setState({ isConfirmationFiltersOpen: false })}
          >
            <form
              className="utmEligiblePanel"
              onMouseDown={event => event.stopPropagation()}
              onSubmit={event => {
                event.preventDefault();
                this.setState({ isConfirmationFiltersOpen: false });
                this.loadConfirmationRequests(1, confirmationSearch, confirmationStatusDraft);
              }}
            >
              <header>
                <h2>Filters</h2>
                <button type="button" onClick={() => this.setState({ isConfirmationFiltersOpen: false })}>
                  ×
                </button>
              </header>
              <div className="utmEligiblePanel__body">
                <section>
                  <h3>Status</h3>
                  <div className="utmEligiblePanel__chips">
                    {[
                      [CONFIRMATION_REQUESTS_STATUS.NOT_READY, 'Awaiting response'],
                      [CONFIRMATION_REQUESTS_STATUS.ACCEPTED, 'Accepted'],
                      [CONFIRMATION_REQUESTS_STATUS.REJECTED, 'Rejected'],
                      [CONFIRMATION_REQUESTS_STATUS.OUTDATED, 'Outdated']
                    ].map(([value, label]) => (
                      <button
                        type="button"
                        key={value}
                        className={confirmationStatusDraft === value ? 'is-selected' : ''}
                        onClick={() =>
                          this.setState({ confirmationStatusDraft: confirmationStatusDraft === value ? '' : value })
                        }
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </section>
              </div>
              <footer>
                <button type="button" onClick={() => this.setState({ confirmationStatusDraft: '' })}>
                  Clear all
                </button>
                <button type="button" onClick={() => this.setState({ isConfirmationFiltersOpen: false })}>
                  Cancel
                </button>
                <button className="is-primary" type="submit">
                  Apply
                </button>
              </footer>
            </form>
          </div>
        ) : null}

        {this.state.isConfirmationColumnsOpen ? (
          <div className="utmColumnsBackdrop" onMouseDown={() => this.setState({ isConfirmationColumnsOpen: false })}>
            <div className="utmColumnsModal" onMouseDown={event => event.stopPropagation()}>
              <header>
                <h2>Table columns</h2>
                <button type="button" className="utmColumnsReset" onClick={this.resetConfirmationColumns}>
                  Reset
                </button>
                <button type="button" onClick={() => this.setState({ isConfirmationColumnsOpen: false })}>
                  ×
                </button>
              </header>
              <div>
                <label>
                  <input type="checkbox" checked disabled />
                  <span>School</span>
                </label>
                {[
                  ['status', 'Status'],
                  ['respondedBy', 'Responded by'],
                  ['respondedAt', 'Responded at'],
                  ['deadline', 'Deadline']
                ].map(([key, label]) => (
                  <label key={key}>
                    <input
                      type="checkbox"
                      checked={confirmationVisibleColumns.includes(key)}
                      onChange={() => this.toggleConfirmationColumn(key)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <footer>
                <button type="button" onClick={() => this.setState({ isConfirmationColumnsOpen: false })}>
                  Done
                </button>
              </footer>
            </div>
          </div>
        ) : null}

        {openedConfirmationRequest ? (
          <div
            className="utmConfirmationDrawer"
            onMouseDown={event =>
              event.target === event.currentTarget && this.setState({ openedConfirmationRequest: undefined })
            }
          >
            <aside>
              <header>
                <div>
                  <h3>{openedConfirmationRequest.participantSchoolData?.name || 'Confirmation request'}</h3>
                  <p>Confirmation request</p>
                </div>
                <button type="button" onClick={() => this.setState({ openedConfirmationRequest: undefined })}>
                  ×
                </button>
              </header>
              <div className="utmConfirmationDrawer__body">
                <span
                  className={`utmConfirmationRequests__status ${
                    statusDetails(openedConfirmationRequest.invitationStatus).className
                  }`}
                >
                  {statusDetails(openedConfirmationRequest.invitationStatus).label}
                </span>
                <dl>
                  <div>
                    <dt>Responded by</dt>
                    <dd>{getResponseUser(openedConfirmationRequest).fullName || '—'}</dd>
                  </div>
                  <div>
                    <dt>Email</dt>
                    <dd>{getResponseUser(openedConfirmationRequest).email || '—'}</dd>
                  </div>
                  <div>
                    <dt>Responded at</dt>
                    <dd>{getRespondedAtText(openedConfirmationRequest) || '—'}</dd>
                  </div>
                  <div>
                    <dt>Deadline</dt>
                    <dd>{formatDate(openedConfirmationRequest.deadlineForAnswers)}</dd>
                  </div>
                </dl>
                {Array.isArray(openedConfirmationRequest.fields) && openedConfirmationRequest.fields.length ? (
                  <section>
                    <h4>Responses</h4>
                    {openedConfirmationRequest.fields.map((field, index) => (
                      <div className="utmConfirmationDrawer__answer" key={index}>
                        <span>{field.heading}</span>
                        <strong>
                          {typeof field.value === 'boolean' ? (field.value ? 'Yes' : 'No') : field.value || '—'}
                        </strong>
                      </div>
                    ))}
                  </section>
                ) : null}
              </div>
              <footer>
                <button type="button" onClick={() => this.setState({ openedConfirmationRequest: undefined })}>
                  Close
                </button>
              </footer>
            </aside>
          </div>
        ) : null}

        {this.state.isSendRequestsOpen ? (
          <div
            className={`utmSendRequestsModal ${
              this.state.confirmationSendStep === 'form' ? 'is-form-step' : 'is-recipients-step'
            }`}
            onMouseDown={event =>
              event.target === event.currentTarget &&
              !this.state.isSendingConfirmationRequests &&
              this.requestCloseConfirmationSend()
            }
          >
            <section>
              <header>
                <div>
                  <h3>Send confirmation requests</h3>
                  <p>{this.props.tournament.name}</p>
                </div>
                <button type="button" onClick={this.requestCloseConfirmationSend}>
                  ×
                </button>
              </header>
              <div className="utmSendRequestsModal__body">
                {this.state.confirmationSendStep === 'form' ? (
                  this.state.confirmationTemplate ? (
                    <ConfirmationRequestForm
                      customClassName="utmSendRequestsModal__formEditor"
                      templateType="TOURNAMENT_CONFIRMATION"
                      user={this.props.user}
                      template={this.state.confirmationTemplate}
                      onCancelClick={this.requestCloseConfirmationSend}
                      onSendConfirmationRequestsClick={this.prepareConfirmationRequest}
                      isRenderDeadline={true}
                      defaultBuilderMode="COMPACT"
                      textSubmitButton="Continue"
                    />
                  ) : (
                    <div className="utmSendRequestsModal__loading">Loading request form…</div>
                  )
                ) : (
                  <>
                    <div className="utmSendRequestsModal__stepSummary">
                      <button type="button" onClick={() => this.setState({ confirmationSendStep: 'form' })}>
                        ← Edit form and questions
                      </button>
                      <span>
                        {this.state.preparedConfirmationFields.length} questions
                        {this.state.confirmationDeadline
                          ? ` · replies by ${moment(this.state.confirmationDeadline).format('DD-MM-YYYY HH:mm')}`
                          : ' · no deadline'}
                      </span>
                    </div>
                    <div className="utmSendRequestsModal__recipientHeader">
                      <strong>
                        Recipients <b>{this.state.selectedConfirmationRecipientIds.length}</b>
                      </strong>
                      <button
                        type="button"
                        onClick={() =>
                          this.setState(state => ({
                            selectedConfirmationRecipientIds:
                              state.selectedConfirmationRecipientIds.length === state.confirmationRecipients.length
                                ? []
                                : state.confirmationRecipients.map(recipient => String(recipient.schoolId))
                          }))
                        }
                      >
                        {this.state.selectedConfirmationRecipientIds.length === this.state.confirmationRecipients.length
                          ? 'Clear all'
                          : 'Select all'}
                      </button>
                    </div>
                    <div className="utmSendRequestsModal__recipients">
                      {this.state.confirmationRecipients.map(recipient => {
                        const id = String(recipient.schoolId);
                        const checked = this.state.selectedConfirmationRecipientIds.includes(id);
                        return (
                          <label key={id}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                this.setState(state => ({
                                  selectedConfirmationRecipientIds: checked
                                    ? state.selectedConfirmationRecipientIds.filter(value => value !== id)
                                    : [...state.selectedConfirmationRecipientIds, id]
                                }))
                              }
                            />
                            <span>{recipient.school?.name || recipient.schoolName || 'Unknown school'}</span>
                          </label>
                        );
                      })}
                      {!this.state.confirmationRecipients.length && !this.state.confirmationSendError ? (
                        <p>No participant schools are currently eligible for a new request.</p>
                      ) : null}
                    </div>
                  </>
                )}
                {this.state.confirmationSendError ? (
                  <div className="utmSendRequestsModal__error">{this.state.confirmationSendError}</div>
                ) : null}
              </div>
              {this.state.confirmationSendStep === 'recipients' ? (
                <footer>
                  <span>{this.state.selectedConfirmationRecipientIds.length} schools will receive a request</span>
                  <div>
                    <button type="button" onClick={this.requestCloseConfirmationSend}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="is-primary"
                      disabled={
                        !this.state.selectedConfirmationRecipientIds.length || this.state.isSendingConfirmationRequests
                      }
                      onClick={this.sendNewConfirmationRequests}
                    >
                      <Icon path="M22 2L11 13M22 2l-7 20-4-9-9-4z" />
                      {this.state.isSendingConfirmationRequests ? 'Sending…' : 'Send requests'}
                    </button>
                  </div>
                </footer>
              ) : null}
            </section>
          </div>
        ) : null}
        <UnsavedChangesModal
          isOpen={this.state.isDiscardConfirmationSendOpen}
          message="Your confirmation request form and recipient selection have not been sent. If you leave now, this work will be lost."
          onKeepEditing={() => this.setState({ isDiscardConfirmationSendOpen: false })}
          onDiscard={this.discardConfirmationSend}
        />
      </div>
    );
  }

  render() {
    return (
      <main className="utmEntries">
        {!this.props.hideSectionNavigation ? <nav className="utmEntries__subtabs" aria-label="Tournament entry sections">
          {SECTIONS.filter(section => section.key !== 'athleteSchools' || hasUnionParticipant(this.props.tournament)).map(section => (
            <button
              key={section.key}
              type="button"
              className={section.key === this.props.activeSection ? 'is-active' : ''}
              aria-current={section.key === this.props.activeSection ? 'page' : undefined}
              onClick={() => this.props.onSectionChange(section.key)}
            >
              <span>{section.label}</span>
              {section.count ? <b>{this.state.counts[section.count]}</b> : null}
            </button>
          ))}
        </nav> : null}
        {this.props.activeSection === 'rules'
          ? this.renderRules()
          : this.props.activeSection === 'eligible'
          ? this.renderEligibleSchools()
          : this.props.activeSection === 'participants'
          ? this.renderParticipants()
          : this.props.activeSection === 'athleteSchools'
          ? <TournamentAthleteSchoolsEntries user={this.props.user} tournament={this.props.tournament} onNotifySchools={this.props.onNotifyAthleteSchools || (() => {})} onCountChanged={this.onAthleteSchoolsCountChanged} />
          : this.props.activeSection === 'schoolAdmins'
          ? this.renderSchoolAdmins()
          : this.props.activeSection === 'waiting'
          ? this.renderWaitingList()
          : this.props.activeSection === 'requests'
          ? this.renderConfirmationRequests()
          : this.props.activeSection === 'log'
          ? this.renderEnrollmentLog()
          : this.renderSectionMessage()}
      </main>
    );
  }
}
